/** Declarative built-in definitions transcribed from master specification chapter 15. No executable application behavior. */
export interface BlockColumn {
  name: string;
  domain: string;
  nullable: boolean;
  description: string;
  /** Explicit stable template port used by industry packs. */
  reference?: string;
}
export interface BlockEntity {
  referenceMode?: "explicit";
  name: string;
  title: string;
  description: string;
  columns: BlockColumn[];
  keys: { role: string; members: string[] }[];
  rules: string;
  derived?: boolean;
}
export const blockDefinitions: Record<string, BlockEntity[]> = {
  "identity-access": [
    {
      name: "iam_realm",
      title: "Realm",
      description: "Access partition and namespace root",
      columns: [
        {
          name: "realm_id",
          domain: "ID",
          nullable: false,
          description: "Immutable realm identity",
        },
        {
          name: "realm_code",
          domain: "CODE(64)",
          nullable: false,
          description:
            "Unique stable realm code under declared comparison policy",
        },
        {
          name: "realm_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "Business display name",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "`active`, `suspended`, or `retired`",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["realm_id"],
        },
        {
          role: "business",
          members: ["realm_code"],
        },
      ],
      rules: "`realm_code`",
    },
    {
      name: "iam_account",
      title: "Account",
      description: "Human or service-facing account identity",
      columns: [
        {
          name: "account_id",
          domain: "ID",
          nullable: false,
          description: "Immutable account identity",
        },
        {
          name: "realm_id",
          domain: "ID",
          nullable: false,
          description: "Owning access realm",
        },
        {
          name: "account_key",
          domain: "CODE(128)",
          nullable: false,
          description:
            "Realm-unique account name/key; normalization policy explicit",
        },
        {
          name: "account_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "`human` or `service`",
        },
        {
          name: "display_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "Friendly account label",
        },
        {
          name: "email",
          domain: "TEXT(320)",
          nullable: true,
          description: "Contact address; deliberately not the unique identity",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "`pending`, `active`, `disabled`, or `retired`",
        },
        {
          name: "created_at",
          domain: "INSTANT",
          nullable: false,
          description: "Account creation event time supplied by application",
        },
        {
          name: "disabled_at",
          domain: "INSTANT",
          nullable: true,
          description:
            "Latest disabling event; not a replacement for event history",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["account_id"],
        },
        {
          role: "business",
          members: ["realm_id", "account_id"],
        },
        {
          role: "business",
          members: ["realm_id", "account_key"],
        },
      ],
      rules: "`(realm_id, account_id)`, `(realm_id, account_key)`",
    },
    {
      name: "iam_identity_provider",
      title: "Identity Provider",
      description: "External identity authority configuration metadata",
      columns: [
        {
          name: "provider_id",
          domain: "ID",
          nullable: false,
          description: "Immutable authority configuration identity",
        },
        {
          name: "realm_id",
          domain: "ID",
          nullable: false,
          description: "Realm in which the authority is accepted",
        },
        {
          name: "provider_code",
          domain: "CODE(64)",
          nullable: false,
          description: "Realm-unique reference code",
        },
        {
          name: "protocol",
          domain: "CODE(16)",
          nullable: false,
          description:
            "Declared `oidc` or `saml`; not proof of connector support",
        },
        {
          name: "issuer",
          domain: "TEXT(512)",
          nullable: false,
          description:
            "Authority identifier compared using protocol-specific rules",
        },
        {
          name: "display_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "User-facing authority label",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "`active`, `disabled`, or `retired`",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["provider_id"],
        },
        {
          role: "business",
          members: ["realm_id", "provider_id"],
        },
        {
          role: "business",
          members: ["realm_id", "provider_code"],
        },
      ],
      rules: "`(realm_id, provider_id)`, `(realm_id, provider_code)`",
    },
    {
      name: "iam_external_identity",
      title: "External Identity",
      description: "Provider subject mapped to an account",
      columns: [
        {
          name: "identity_id",
          domain: "ID",
          nullable: false,
          description: "Immutable external mapping identity",
        },
        {
          name: "realm_id",
          domain: "ID",
          nullable: false,
          description: "Scope used in composite references",
        },
        {
          name: "account_id",
          domain: "ID",
          nullable: false,
          description: "Account represented by this external subject",
        },
        {
          name: "provider_id",
          domain: "ID",
          nullable: false,
          description: "Configured external authority",
        },
        {
          name: "subject_value",
          domain: "TEXT(255)",
          nullable: false,
          description:
            "Opaque provider subject; case-sensitive exact comparison required",
        },
        {
          name: "created_at",
          domain: "INSTANT",
          nullable: false,
          description: "Mapping creation time",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["identity_id"],
        },
        {
          role: "business",
          members: ["provider_id", "subject_value"],
        },
      ],
      rules: "`(provider_id, subject_value)`",
    },
    {
      name: "iam_role",
      title: "Role",
      description: "Named bundle of permissions within a realm",
      columns: [
        {
          name: "role_id",
          domain: "ID",
          nullable: false,
          description: "Immutable role identity",
        },
        {
          name: "realm_id",
          domain: "ID",
          nullable: false,
          description: "Owning realm",
        },
        {
          name: "role_code",
          domain: "CODE(64)",
          nullable: false,
          description: "Realm-unique role code",
        },
        {
          name: "role_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "Human-readable role label",
        },
        {
          name: "description",
          domain: "TEXT(2000)",
          nullable: true,
          description: "Intended responsibility and usage",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "`active`, `disabled`, or `retired`",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["role_id"],
        },
        {
          role: "business",
          members: ["realm_id", "role_id"],
        },
        {
          role: "business",
          members: ["realm_id", "role_code"],
        },
      ],
      rules: "`(realm_id, role_id)`, `(realm_id, role_code)`",
    },
    {
      name: "iam_resource",
      title: "Resource",
      description: "Protected screen, API, entity, or operation target",
      columns: [
        {
          name: "resource_id",
          domain: "ID",
          nullable: false,
          description: "Immutable protected resource identity",
        },
        {
          name: "realm_id",
          domain: "ID",
          nullable: false,
          description: "Owning realm",
        },
        {
          name: "resource_code",
          domain: "CODE(128)",
          nullable: false,
          description: "Realm-unique application reference",
        },
        {
          name: "resource_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "`screen`, `api`, `entity`, or `operation`",
        },
        {
          name: "resource_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "Friendly name",
        },
        {
          name: "parent_resource_id",
          domain: "ID",
          nullable: true,
          description: "Optional hierarchy parent in the same realm",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "`active`, `disabled`, or `retired`",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["resource_id"],
        },
        {
          role: "business",
          members: ["realm_id", "resource_id"],
        },
        {
          role: "business",
          members: ["realm_id", "resource_code"],
        },
      ],
      rules: "`(realm_id, resource_id)`, `(realm_id, resource_code)`",
    },
    {
      name: "iam_action",
      title: "Action",
      description: "Reusable action vocabulary",
      columns: [
        {
          name: "action_id",
          domain: "ID",
          nullable: false,
          description: "Immutable action identity",
        },
        {
          name: "action_code",
          domain: "CODE(32)",
          nullable: false,
          description:
            "Unique action such as `view`, `create`, `update`, `delete`, `approve`",
        },
        {
          name: "action_name",
          domain: "TEXT(100)",
          nullable: false,
          description: "Friendly action label",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["action_id"],
        },
        {
          role: "business",
          members: ["action_code"],
        },
      ],
      rules: "`action_code`",
    },
    {
      name: "iam_permission",
      title: "Permission",
      description: "One allowed action on a resource",
      columns: [
        {
          name: "permission_id",
          domain: "ID",
          nullable: false,
          description: "Immutable entitlement identity",
        },
        {
          name: "realm_id",
          domain: "ID",
          nullable: false,
          description: "Scope shared by resource and receiving roles",
        },
        {
          name: "resource_id",
          domain: "ID",
          nullable: false,
          description: "Protected target",
        },
        {
          name: "action_id",
          domain: "ID",
          nullable: false,
          description: "Allowed action vocabulary reference",
        },
        {
          name: "description",
          domain: "TEXT(2000)",
          nullable: true,
          description: "Intent or restrictions not encoded elsewhere",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["permission_id"],
        },
        {
          role: "business",
          members: ["realm_id", "permission_id"],
        },
        {
          role: "business",
          members: ["realm_id", "resource_id", "action_id"],
        },
      ],
      rules:
        "`(realm_id, permission_id)`, `(realm_id, resource_id, action_id)`",
    },
    {
      name: "iam_role_permission",
      title: "Role Permission",
      description: "Grants permission to a role",
      columns: [
        {
          name: "realm_id",
          domain: "ID",
          nullable: false,
          description: "Shared realm of role and permission",
        },
        {
          name: "role_id",
          domain: "ID",
          nullable: false,
          description: "Receiving role",
        },
        {
          name: "permission_id",
          domain: "ID",
          nullable: false,
          description: "Granted allow permission",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["realm_id", "role_id", "permission_id"],
        },
      ],
      rules: "None",
    },
    {
      name: "iam_account_role",
      title: "Account Role",
      description: "Effective-dated direct account role assignment",
      columns: [
        {
          name: "assignment_id",
          domain: "ID",
          nullable: false,
          description: "Immutable assignment occurrence",
        },
        {
          name: "realm_id",
          domain: "ID",
          nullable: false,
          description: "Shared account/role scope",
        },
        {
          name: "account_id",
          domain: "ID",
          nullable: false,
          description: "Receiving account",
        },
        {
          name: "role_id",
          domain: "ID",
          nullable: false,
          description: "Assigned role",
        },
        {
          name: "valid_from",
          domain: "INSTANT",
          nullable: false,
          description: "Inclusive activation instant",
        },
        {
          name: "valid_to",
          domain: "INSTANT",
          nullable: true,
          description: "Exclusive end; null means open-ended",
        },
        {
          name: "assignment_reason",
          domain: "TEXT(1000)",
          nullable: true,
          description: "Business justification, no secret content",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["assignment_id"],
        },
        {
          role: "business",
          members: ["realm_id", "account_id", "role_id", "valid_from"],
        },
      ],
      rules: "`(realm_id, account_id, role_id, valid_from)`",
    },
    {
      name: "iam_security_group",
      title: "Security Group",
      description: "Account grouping for entitlement assignment",
      columns: [
        {
          name: "group_id",
          domain: "ID",
          nullable: false,
          description: "Immutable group identity",
        },
        {
          name: "realm_id",
          domain: "ID",
          nullable: false,
          description: "Owning realm",
        },
        {
          name: "group_code",
          domain: "CODE(64)",
          nullable: false,
          description: "Realm-unique group code",
        },
        {
          name: "group_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "Display name",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "`active`, `disabled`, or `retired`",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["group_id"],
        },
        {
          role: "business",
          members: ["realm_id", "group_id"],
        },
        {
          role: "business",
          members: ["realm_id", "group_code"],
        },
      ],
      rules: "`(realm_id, group_id)`, `(realm_id, group_code)`",
    },
    {
      name: "iam_group_member",
      title: "Group Member",
      description: "Effective-dated account membership in a group",
      columns: [
        {
          name: "membership_id",
          domain: "ID",
          nullable: false,
          description: "Immutable membership occurrence",
        },
        {
          name: "realm_id",
          domain: "ID",
          nullable: false,
          description: "Shared group/account realm",
        },
        {
          name: "group_id",
          domain: "ID",
          nullable: false,
          description: "Parent security group",
        },
        {
          name: "account_id",
          domain: "ID",
          nullable: false,
          description: "Member account",
        },
        {
          name: "valid_from",
          domain: "INSTANT",
          nullable: false,
          description: "Inclusive membership start",
        },
        {
          name: "valid_to",
          domain: "INSTANT",
          nullable: true,
          description: "Exclusive membership end",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["membership_id"],
        },
        {
          role: "business",
          members: ["realm_id", "group_id", "account_id", "valid_from"],
        },
      ],
      rules: "`(realm_id, group_id, account_id, valid_from)`",
    },
    {
      name: "iam_group_role",
      title: "Group Role",
      description: "Effective-dated role assignment to a group",
      columns: [
        {
          name: "assignment_id",
          domain: "ID",
          nullable: false,
          description: "Immutable group grant occurrence",
        },
        {
          name: "realm_id",
          domain: "ID",
          nullable: false,
          description: "Shared group/role realm",
        },
        {
          name: "group_id",
          domain: "ID",
          nullable: false,
          description: "Receiving security group",
        },
        {
          name: "role_id",
          domain: "ID",
          nullable: false,
          description: "Assigned role",
        },
        {
          name: "valid_from",
          domain: "INSTANT",
          nullable: false,
          description: "Inclusive grant start",
        },
        {
          name: "valid_to",
          domain: "INSTANT",
          nullable: true,
          description: "Exclusive grant end",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["assignment_id"],
        },
        {
          role: "business",
          members: ["realm_id", "group_id", "role_id", "valid_from"],
        },
      ],
      rules: "`(realm_id, group_id, role_id, valid_from)`",
    },
    {
      name: "iam_authentication_policy",
      title: "Authentication Policy",
      description: "Declared SSO/MFA behavior for one realm",
      columns: [
        {
          name: "policy_id",
          domain: "ID",
          nullable: false,
          description: "Immutable declared policy identity",
        },
        {
          name: "realm_id",
          domain: "ID",
          nullable: false,
          description:
            "At most one current policy row per realm in this profile",
        },
        {
          name: "policy_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "Friendly policy label",
        },
        {
          name: "sso_mode",
          domain: "CODE(16)",
          nullable: false,
          description: "`disabled`, `optional`, or `required`",
        },
        {
          name: "local_login_allowed",
          domain: "BOOL",
          nullable: false,
          description:
            "Declared application policy; no credentials stored here",
        },
        {
          name: "mfa_mode",
          domain: "CODE(16)",
          nullable: false,
          description: "`disabled`, `optional`, or `required`",
        },
        {
          name: "enforcement_owner",
          domain: "TEXT(200)",
          nullable: false,
          description:
            "Identified runtime/provider responsible for enforcement",
        },
        {
          name: "description",
          domain: "TEXT(2000)",
          nullable: true,
          description:
            "Exceptions, assurance expectations, and implementation notes",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["policy_id"],
        },
        {
          role: "business",
          members: ["realm_id"],
        },
      ],
      rules: "`realm_id`",
    },
  ],
  "organization-employee": [
    {
      name: "org_organization",
      title: "Organization",
      description: "Legal or administrative organization root",
      columns: [
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Immutable organization identity",
        },
        {
          name: "organization_code",
          domain: "CODE(64)",
          nullable: false,
          description: "Unique administrative code",
        },
        {
          name: "organization_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "Display name",
        },
        {
          name: "legal_name",
          domain: "TEXT(300)",
          nullable: true,
          description: "Registered name if relevant",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "`planned`, `active`, or `retired`",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["organization_id"],
        },
        {
          role: "business",
          members: ["organization_code"],
        },
      ],
      rules: "`organization_code`",
    },
    {
      name: "org_unit",
      title: "Unit",
      description: "Hierarchical department/division/team",
      columns: [
        {
          name: "unit_id",
          domain: "ID",
          nullable: false,
          description: "Immutable organization-unit identity",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Owning organization",
        },
        {
          name: "unit_code",
          domain: "CODE(64)",
          nullable: false,
          description: "Organization-unique unit code",
        },
        {
          name: "unit_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "Friendly unit name",
        },
        {
          name: "unit_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "`division`, `department`, `team`, or `other`",
        },
        {
          name: "parent_unit_id",
          domain: "ID",
          nullable: true,
          description: "Same-organization hierarchy parent",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "`planned`, `active`, or `retired`",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["unit_id"],
        },
        {
          role: "business",
          members: ["organization_id", "unit_id"],
        },
        {
          role: "business",
          members: ["organization_id", "unit_code"],
        },
      ],
      rules: "`(organization_id, unit_id)`, `(organization_id, unit_code)`",
    },
    {
      name: "org_position",
      title: "Position",
      description: "Authorized or planned role in an organizational unit",
      columns: [
        {
          name: "position_id",
          domain: "ID",
          nullable: false,
          description: "Immutable position identity",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Scope for the position and unit reference",
        },
        {
          name: "unit_id",
          domain: "ID",
          nullable: false,
          description: "Owning organization unit",
        },
        {
          name: "position_code",
          domain: "CODE(64)",
          nullable: false,
          description: "Organization-unique position code",
        },
        {
          name: "position_title",
          domain: "TEXT(200)",
          nullable: false,
          description: "Role title; not a permission grant",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "`planned`, `open`, `filled`, or `retired`",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["position_id"],
        },
        {
          role: "business",
          members: ["organization_id", "position_id"],
        },
        {
          role: "business",
          members: ["organization_id", "position_code"],
        },
      ],
      rules:
        "`(organization_id, position_id)`, `(organization_id, position_code)`",
    },
    {
      name: "org_employee",
      title: "Employee",
      description: "Employee identity within an organization",
      columns: [
        {
          name: "employee_id",
          domain: "ID",
          nullable: false,
          description: "Immutable employee identity in this profile",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization owning the employee record",
        },
        {
          name: "employee_number",
          domain: "CODE(64)",
          nullable: false,
          description: "Organization-unique employee number",
        },
        {
          name: "given_name",
          domain: "TEXT(150)",
          nullable: true,
          description: "Optional culturally dependent name component",
        },
        {
          name: "family_name",
          domain: "TEXT(150)",
          nullable: true,
          description: "Optional culturally dependent name component",
        },
        {
          name: "display_name",
          domain: "TEXT(300)",
          nullable: false,
          description:
            "Required usable name without assumptions about name structure",
        },
        {
          name: "work_email",
          domain: "TEXT(320)",
          nullable: true,
          description: "Contact address, not identity or automatic join key",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "`prospective`, `active`, `inactive`, or `retired`",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["employee_id"],
        },
        {
          role: "business",
          members: ["organization_id", "employee_id"],
        },
        {
          role: "business",
          members: ["organization_id", "employee_number"],
        },
      ],
      rules:
        "`(organization_id, employee_id)`, `(organization_id, employee_number)`",
    },
    {
      name: "org_employment_type",
      title: "Employment Type",
      description: "Reusable employment classification vocabulary",
      columns: [
        {
          name: "type_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Governed employment category code",
        },
        {
          name: "type_name",
          domain: "TEXT(150)",
          nullable: false,
          description: "Human-readable category",
        },
        {
          name: "description",
          domain: "TEXT(2000)",
          nullable: true,
          description: "Category boundaries and usage",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "`active` or `retired`",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["type_code"],
        },
      ],
      rules: "None",
    },
    {
      name: "org_employment",
      title: "Employment",
      description: "Employment occurrence with dates and type",
      columns: [
        {
          name: "employment_id",
          domain: "ID",
          nullable: false,
          description: "Immutable occurrence identity",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Shared organization scope",
        },
        {
          name: "employee_id",
          domain: "ID",
          nullable: false,
          description: "Employee holding this employment",
        },
        {
          name: "type_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Employment category",
        },
        {
          name: "start_date",
          domain: "DATE",
          nullable: false,
          description: "Inclusive business start date",
        },
        {
          name: "end_date",
          domain: "DATE",
          nullable: true,
          description: "Exclusive business end date",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "`planned`, `active`, `leave`, or `ended`",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["employment_id"],
        },
        {
          role: "business",
          members: ["organization_id", "employment_id"],
        },
      ],
      rules: "`(organization_id, employment_id)`",
    },
    {
      name: "org_position_assignment",
      title: "Position Assignment",
      description: "Effective assignment of an employment to a position",
      columns: [
        {
          name: "assignment_id",
          domain: "ID",
          nullable: false,
          description: "Immutable assignment occurrence",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Shared employment/position scope",
        },
        {
          name: "employment_id",
          domain: "ID",
          nullable: false,
          description: "Assigned employment occurrence",
        },
        {
          name: "position_id",
          domain: "ID",
          nullable: false,
          description: "Position occupied",
        },
        {
          name: "valid_from",
          domain: "DATE",
          nullable: false,
          description: "Inclusive assignment start date",
        },
        {
          name: "valid_to",
          domain: "DATE",
          nullable: true,
          description: "Exclusive assignment end date",
        },
        {
          name: "allocation_percent",
          domain: "DECIMAL(5,2)",
          nullable: false,
          description:
            "Percentage in 0–100 representation; positive and at most 100",
        },
        {
          name: "is_primary",
          domain: "BOOL",
          nullable: false,
          description: "Declared primary assignment indicator",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["assignment_id"],
        },
        {
          role: "business",
          members: [
            "organization_id",
            "employment_id",
            "position_id",
            "valid_from",
          ],
        },
      ],
      rules: "`(organization_id, employment_id, position_id, valid_from)`",
    },
    {
      name: "org_reporting_line",
      title: "Reporting Line",
      description: "Effective manager/subordinate relationship",
      columns: [
        {
          name: "reporting_line_id",
          domain: "ID",
          nullable: false,
          description: "Immutable reporting relationship occurrence",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Shared organization scope",
        },
        {
          name: "subordinate_employment_id",
          domain: "ID",
          nullable: false,
          description: "Reporting employment occurrence",
        },
        {
          name: "manager_employment_id",
          domain: "ID",
          nullable: false,
          description: "Manager's employment occurrence",
        },
        {
          name: "line_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "`line`, `functional`, or `project`",
        },
        {
          name: "valid_from",
          domain: "DATE",
          nullable: false,
          description: "Inclusive relationship start",
        },
        {
          name: "valid_to",
          domain: "DATE",
          nullable: true,
          description: "Exclusive relationship end",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["reporting_line_id"],
        },
        {
          role: "business",
          members: [
            "organization_id",
            "subordinate_employment_id",
            "manager_employment_id",
            "line_kind",
            "valid_from",
          ],
        },
      ],
      rules:
        "`(organization_id, subordinate_employment_id, manager_employment_id, line_kind, valid_from)`",
    },
    {
      name: "org_location",
      title: "Location",
      description: "Workplace or business location",
      columns: [
        {
          name: "location_id",
          domain: "ID",
          nullable: false,
          description: "Immutable location identity",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Owning organization",
        },
        {
          name: "location_code",
          domain: "CODE(64)",
          nullable: false,
          description: "Organization-unique workplace code",
        },
        {
          name: "location_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "Friendly workplace name",
        },
        {
          name: "country_code",
          domain: "CODE(2)",
          nullable: true,
          description: "Optional configured country-code vocabulary reference",
        },
        {
          name: "time_zone",
          domain: "TEXT(100)",
          nullable: true,
          description: "Named time zone for business-date interpretation",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["location_id"],
        },
        {
          role: "business",
          members: ["organization_id", "location_id"],
        },
        {
          role: "business",
          members: ["organization_id", "location_code"],
        },
      ],
      rules:
        "`(organization_id, location_id)`, `(organization_id, location_code)`",
    },
    {
      name: "org_unit_location",
      title: "Unit Location",
      description: "Association of a unit with a location and purpose",
      columns: [
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Shared unit/location scope",
        },
        {
          name: "unit_id",
          domain: "ID",
          nullable: false,
          description: "Organization unit",
        },
        {
          name: "location_id",
          domain: "ID",
          nullable: false,
          description: "Associated workplace",
        },
        {
          name: "location_role",
          domain: "CODE(16)",
          nullable: false,
          description: "`primary`, `secondary`, or `service`",
        },
      ],
      keys: [
        {
          role: "primary",
          members: [
            "organization_id",
            "unit_id",
            "location_id",
            "location_role",
          ],
        },
      ],
      rules: "None",
    },
  ],
  party: [
    {
      name: "pty_party",
      title: "Party",
      description: "Party. PK party_id; display name deliberately not unique",
      columns: [
        {
          name: "party_id",
          domain: "ID",
          nullable: false,
          description: "stable shared identity",
        },
        {
          name: "party_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "person/organization/undetermined",
        },
        {
          name: "display_name",
          domain: "TEXT(300)",
          nullable: false,
          description: "display label",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "active/retired",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["party_id"],
        },
      ],
      rules: "PK party_id; display name deliberately not unique",
    },
    {
      name: "pty_person",
      title: "Person",
      description:
        "Person. PK/FK party_id -> Party; person-kind rule requires separate subtype enforcement",
      columns: [
        {
          name: "party_id",
          domain: "ID",
          nullable: false,
          description: "Party identity",
        },
        {
          name: "given_name",
          domain: "TEXT(150)",
          nullable: true,
          description: "Given name",
        },
        {
          name: "family_name",
          domain: "TEXT(150)",
          nullable: true,
          description: "optional name components",
        },
        {
          name: "preferred_name",
          domain: "TEXT(300)",
          nullable: true,
          description: "preferred address",
        },
        {
          name: "birth_date",
          domain: "DATE",
          nullable: true,
          description: "optional sensitive biographical fact",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["party_id"],
        },
      ],
      rules:
        "PK/FK party_id -> Party; person-kind rule requires separate subtype enforcement",
    },
    {
      name: "pty_organization",
      title: "Party organization",
      description:
        "Party organization. PK/FK party_id -> Party; names not automatic candidate keys",
      columns: [
        {
          name: "party_id",
          domain: "ID",
          nullable: false,
          description: "Party id",
        },
        {
          name: "legal_name",
          domain: "TEXT(300)",
          nullable: true,
          description: "registered name",
        },
        {
          name: "trading_name",
          domain: "TEXT(300)",
          nullable: true,
          description: "Trading name",
        },
        {
          name: "organization_kind",
          domain: "CODE(32)",
          nullable: true,
          description: "configured classification",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["party_id"],
        },
      ],
      rules: "PK/FK party_id -> Party; names not automatic candidate keys",
    },
    {
      name: "pty_role_type",
      title: "Role type",
      description:
        "Role type. PK role_type_code; vocabulary is editable through reviewed reference-data profile",
      columns: [
        {
          name: "role_type_code",
          domain: "CODE(32)",
          nullable: false,
          description:
            "employee/customer/supplier/candidate/beneficiary or governed extension",
        },
        {
          name: "role_type_name",
          domain: "TEXT(150)",
          nullable: false,
          description: "Role type name",
        },
        {
          name: "description",
          domain: "TEXT(2000)",
          nullable: false,
          description: "role boundaries",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["role_type_code"],
        },
      ],
      rules:
        "PK role_type_code; vocabulary is editable through reviewed reference-data profile",
    },
    {
      name: "pty_role",
      title: "Party role",
      description:
        "Party role. PK party_role_id; BK `(party_id,role_type_code,scope_party_id,valid_from)`; FKs both Party refs and Role type",
      columns: [
        {
          name: "party_role_id",
          domain: "ID",
          nullable: false,
          description: "Party role id",
        },
        {
          name: "party_id",
          domain: "ID",
          nullable: false,
          description: "role bearer",
        },
        {
          name: "role_type_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Role type code",
        },
        {
          name: "scope_party_id",
          domain: "ID",
          nullable: false,
          description: "organization or other declared scope",
        },
        {
          name: "valid_from",
          domain: "DATE",
          nullable: false,
          description: "Valid from",
        },
        {
          name: "valid_to",
          domain: "DATE",
          nullable: true,
          description: "Valid to",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "proposed/active/ended",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["party_role_id"],
        },
        {
          role: "business",
          members: [
            "party_id",
            "role_type_code",
            "scope_party_id",
            "valid_from",
          ],
        },
      ],
      rules:
        "PK party_role_id; BK `(party_id,role_type_code,scope_party_id,valid_from)`; FKs both Party refs and Role type",
    },
    {
      name: "pty_identifier",
      title: "Party identifier",
      description:
        "Party identifier. PK identifier_id; BK `(authority_code,identifier_kind,identifier_value,valid_from)` under selected authority policy; FK Party",
      columns: [
        {
          name: "identifier_id",
          domain: "ID",
          nullable: false,
          description: "Identifier id",
        },
        {
          name: "party_id",
          domain: "ID",
          nullable: false,
          description: "Party id",
        },
        {
          name: "authority_code",
          domain: "CODE(64)",
          nullable: false,
          description: "Authority code",
        },
        {
          name: "identifier_kind",
          domain: "CODE(32)",
          nullable: false,
          description: "Identifier kind",
        },
        {
          name: "identifier_value",
          domain: "TEXT(200)",
          nullable: false,
          description: "Identifier value",
        },
        {
          name: "valid_from",
          domain: "DATE",
          nullable: false,
          description: "Valid from",
        },
        {
          name: "valid_to",
          domain: "DATE",
          nullable: true,
          description: "Valid to",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["identifier_id"],
        },
        {
          role: "business",
          members: [
            "authority_code",
            "identifier_kind",
            "identifier_value",
            "valid_from",
          ],
        },
      ],
      rules:
        "PK identifier_id; BK `(authority_code,identifier_kind,identifier_value,valid_from)` under selected authority policy; FK Party",
    },
    {
      name: "pty_contact",
      title: "Party contact",
      description:
        "Party contact. PK contact_id; FK Party; no default global uniqueness of contact value; preferred-contact rule is temporal",
      columns: [
        {
          name: "contact_id",
          domain: "ID",
          nullable: false,
          description: "Contact id",
        },
        {
          name: "party_id",
          domain: "ID",
          nullable: false,
          description: "Party id",
        },
        {
          name: "contact_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "email/phone/web/other",
        },
        {
          name: "contact_value",
          domain: "TEXT(500)",
          nullable: false,
          description: "Contact value",
        },
        {
          name: "purpose",
          domain: "CODE(32)",
          nullable: false,
          description: "Purpose",
        },
        {
          name: "valid_from",
          domain: "DATE",
          nullable: false,
          description: "Valid from",
        },
        {
          name: "valid_to",
          domain: "DATE",
          nullable: true,
          description: "Valid to",
        },
        {
          name: "is_preferred",
          domain: "BOOL",
          nullable: false,
          description: "Is preferred",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["contact_id"],
        },
      ],
      rules:
        "PK contact_id; FK Party; no default global uniqueness of contact value; preferred-contact rule is temporal",
    },
    {
      name: "pty_address",
      title: "Party address",
      description:
        "Party address. PK address_id; FK Party; repeated shared addresses allowed",
      columns: [
        {
          name: "address_id",
          domain: "ID",
          nullable: false,
          description: "Address id",
        },
        {
          name: "party_id",
          domain: "ID",
          nullable: false,
          description: "Party id",
        },
        {
          name: "address_label",
          domain: "TEXT(100)",
          nullable: true,
          description: "Address label",
        },
        {
          name: "address_text",
          domain: "TEXT(2000)",
          nullable: false,
          description: "culturally flexible postal address",
        },
        {
          name: "country_code",
          domain: "CODE(2)",
          nullable: true,
          description: "Country code",
        },
        {
          name: "valid_from",
          domain: "DATE",
          nullable: false,
          description: "Valid from",
        },
        {
          name: "valid_to",
          domain: "DATE",
          nullable: true,
          description: "Valid to",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["address_id"],
        },
      ],
      rules: "PK address_id; FK Party; repeated shared addresses allowed",
    },
  ],
  talent: [
    {
      name: "tal_requisition",
      title: "Requisition",
      description:
        "Requisition. PK requisition_id; BK `(organization_id,requisition_number)`; optional scoped Position FK",
      columns: [
        {
          name: "requisition_id",
          domain: "ID",
          nullable: false,
          description: "Requisition id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "requisition_number",
          domain: "CODE(64)",
          nullable: false,
          description: "Requisition number",
        },
        {
          name: "position_id",
          domain: "ID",
          nullable: true,
          description: "Position id",
        },
        {
          name: "title",
          domain: "TEXT(200)",
          nullable: false,
          description: "Title",
        },
        {
          name: "open_date",
          domain: "DATE",
          nullable: false,
          description: "Open date",
        },
        {
          name: "close_date",
          domain: "DATE",
          nullable: true,
          description: "Close date",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "draft/open/paused/closed",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["requisition_id"],
        },
        {
          role: "business",
          members: ["organization_id", "requisition_number"],
        },
      ],
      rules:
        "PK requisition_id; BK `(organization_id,requisition_number)`; optional scoped Position FK",
    },
    {
      name: "tal_candidate",
      title: "Candidate",
      description:
        "Candidate. PK candidate_id; BK `(organization_id,candidate_number)`; UQ `(organization_id,person_party_id)` in this profile; FK Person",
      columns: [
        {
          name: "candidate_id",
          domain: "ID",
          nullable: false,
          description: "Candidate id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "recruiting owner",
        },
        {
          name: "person_party_id",
          domain: "ID",
          nullable: false,
          description: "Person party id",
        },
        {
          name: "candidate_number",
          domain: "CODE(64)",
          nullable: false,
          description: "Candidate number",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "prospect/active/inactive/archived",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["candidate_id"],
        },
        {
          role: "business",
          members: ["organization_id", "candidate_number"],
        },
        {
          role: "unique",
          members: ["organization_id", "person_party_id"],
        },
      ],
      rules:
        "PK candidate_id; BK `(organization_id,candidate_number)`; UQ `(organization_id,person_party_id)` in this profile; FK Person",
    },
    {
      name: "tal_application",
      title: "Application",
      description:
        "Application. PK application_id; BK `(organization_id,application_number)`; scoped Requisition and Candidate FKs; repeated applications require distinct numbers",
      columns: [
        {
          name: "application_id",
          domain: "ID",
          nullable: false,
          description: "Application id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "requisition_id",
          domain: "ID",
          nullable: false,
          description: "Requisition id",
        },
        {
          name: "candidate_id",
          domain: "ID",
          nullable: false,
          description: "Candidate id",
        },
        {
          name: "application_number",
          domain: "CODE(64)",
          nullable: false,
          description: "Application number",
        },
        {
          name: "applied_at",
          domain: "INSTANT",
          nullable: false,
          description: "Applied at",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description:
            "submitted/review/interview/offer/hired/rejected/withdrawn",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["application_id"],
        },
        {
          role: "business",
          members: ["organization_id", "application_number"],
        },
      ],
      rules:
        "PK application_id; BK `(organization_id,application_number)`; scoped Requisition and Candidate FKs; repeated applications require distinct numbers",
    },
    {
      name: "tal_stage",
      title: "Stage",
      description:
        "Stage. PK stage_id; BK `(organization_id,stage_code)`; order does not enforce workflow transitions",
      columns: [
        {
          name: "stage_id",
          domain: "ID",
          nullable: false,
          description: "Stage id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "stage_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Stage code",
        },
        {
          name: "stage_name",
          domain: "TEXT(150)",
          nullable: false,
          description: "Stage name",
        },
        {
          name: "sequence_hint",
          domain: "INTEGER",
          nullable: false,
          description: "display order",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["stage_id"],
        },
        {
          role: "business",
          members: ["organization_id", "stage_code"],
        },
      ],
      rules:
        "PK stage_id; BK `(organization_id,stage_code)`; order does not enforce workflow transitions",
    },
    {
      name: "tal_stage_event",
      title: "Application stage event",
      description:
        "Application stage event. PK stage_event_id; BK `(organization_id,application_id,stage_id,entered_at)`; scoped FKs; repeated stage visits allowed",
      columns: [
        {
          name: "stage_event_id",
          domain: "ID",
          nullable: false,
          description: "Stage event id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "application_id",
          domain: "ID",
          nullable: false,
          description: "Application id",
        },
        {
          name: "stage_id",
          domain: "ID",
          nullable: false,
          description: "Stage id",
        },
        {
          name: "entered_at",
          domain: "INSTANT",
          nullable: false,
          description: "Entered at",
        },
        {
          name: "left_at",
          domain: "INSTANT",
          nullable: true,
          description: "Left at",
        },
        {
          name: "outcome",
          domain: "CODE(32)",
          nullable: true,
          description: "Outcome",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["stage_event_id"],
        },
        {
          role: "business",
          members: [
            "organization_id",
            "application_id",
            "stage_id",
            "entered_at",
          ],
        },
      ],
      rules:
        "PK stage_event_id; BK `(organization_id,application_id,stage_id,entered_at)`; scoped FKs; repeated stage visits allowed",
    },
    {
      name: "tal_assessment",
      title: "Assessment",
      description:
        "Assessment. PK assessment_id; scoped Application FK; no arbitrary cross-test score aggregation",
      columns: [
        {
          name: "assessment_id",
          domain: "ID",
          nullable: false,
          description: "Assessment id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "application_id",
          domain: "ID",
          nullable: false,
          description: "Application id",
        },
        {
          name: "assessment_kind",
          domain: "CODE(32)",
          nullable: false,
          description: "Assessment kind",
        },
        {
          name: "assessed_at",
          domain: "INSTANT",
          nullable: false,
          description: "Assessed at",
        },
        {
          name: "outcome_text",
          domain: "TEXT(2000)",
          nullable: false,
          description: "Outcome text",
        },
        {
          name: "evidence_reference",
          domain: "TEXT(500)",
          nullable: true,
          description: "permitted document locator",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["assessment_id"],
        },
      ],
      rules:
        "PK assessment_id; scoped Application FK; no arbitrary cross-test score aggregation",
    },
    {
      name: "tal_offer",
      title: "Offer",
      description:
        "Offer. PK offer_id; BK `(organization_id,offer_number)`; scoped Application FK; multiple historical offers allowed",
      columns: [
        {
          name: "offer_id",
          domain: "ID",
          nullable: false,
          description: "Offer id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "application_id",
          domain: "ID",
          nullable: false,
          description: "Application id",
        },
        {
          name: "offer_number",
          domain: "CODE(64)",
          nullable: false,
          description: "Offer number",
        },
        {
          name: "offered_on",
          domain: "DATE",
          nullable: false,
          description: "Offered on",
        },
        {
          name: "expires_on",
          domain: "DATE",
          nullable: true,
          description: "Expires on",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "draft/issued/accepted/declined/withdrawn/expired",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["offer_id"],
        },
        {
          role: "business",
          members: ["organization_id", "offer_number"],
        },
      ],
      rules:
        "PK offer_id; BK `(organization_id,offer_number)`; scoped Application FK; multiple historical offers allowed",
    },
    {
      name: "tal_hire_handoff",
      title: "Hire handoff",
      description:
        "Hire handoff. PK handoff_id; UQ `(organization_id,offer_id)`; scoped Offer, Employee and Employment FKs; employment must belong to employee",
      columns: [
        {
          name: "handoff_id",
          domain: "ID",
          nullable: false,
          description: "Handoff id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "offer_id",
          domain: "ID",
          nullable: false,
          description: "Offer id",
        },
        {
          name: "employee_id",
          domain: "ID",
          nullable: false,
          description: "Employee id",
        },
        {
          name: "employment_id",
          domain: "ID",
          nullable: false,
          description: "Employment id",
        },
        {
          name: "accepted_at",
          domain: "INSTANT",
          nullable: false,
          description: "Accepted at",
        },
        {
          name: "mapping_source",
          domain: "CODE(32)",
          nullable: false,
          description: "Mapping source",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["handoff_id"],
        },
        {
          role: "unique",
          members: ["organization_id", "offer_id"],
        },
      ],
      rules:
        "PK handoff_id; UQ `(organization_id,offer_id)`; scoped Offer, Employee and Employment FKs; employment must belong to employee",
    },
  ],
  benefits: [
    {
      name: "ben_plan",
      title: "Benefit plan",
      description:
        "Benefit plan. PK plan_id; BK `(organization_id,plan_code,valid_from)`; optional Party FK; provider need not be modeled as Supplier until integrated",
      columns: [
        {
          name: "plan_id",
          domain: "ID",
          nullable: false,
          description: "Plan id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "plan_code",
          domain: "CODE(64)",
          nullable: false,
          description: "Plan code",
        },
        {
          name: "plan_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "Plan name",
        },
        {
          name: "plan_kind",
          domain: "CODE(32)",
          nullable: false,
          description: "Plan kind",
        },
        {
          name: "valid_from",
          domain: "DATE",
          nullable: false,
          description: "Valid from",
        },
        {
          name: "valid_to",
          domain: "DATE",
          nullable: true,
          description: "Valid to",
        },
        {
          name: "provider_party_id",
          domain: "ID",
          nullable: true,
          description: "Provider party id",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["plan_id"],
        },
        {
          role: "business",
          members: ["organization_id", "plan_code", "valid_from"],
        },
      ],
      rules:
        "PK plan_id; BK `(organization_id,plan_code,valid_from)`; optional Party FK; provider need not be modeled as Supplier until integrated",
    },
    {
      name: "ben_option",
      title: "Plan option",
      description:
        "Plan option. PK option_id; BK `(organization_id,plan_id,option_code)`; scoped Plan FK",
      columns: [
        {
          name: "option_id",
          domain: "ID",
          nullable: false,
          description: "Option id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "plan_id",
          domain: "ID",
          nullable: false,
          description: "Plan id",
        },
        {
          name: "option_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Option code",
        },
        {
          name: "option_name",
          domain: "TEXT(150)",
          nullable: false,
          description: "Option name",
        },
        {
          name: "coverage_description",
          domain: "TEXT(2000)",
          nullable: false,
          description: "Coverage description",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["option_id"],
        },
        {
          role: "business",
          members: ["organization_id", "plan_id", "option_code"],
        },
      ],
      rules:
        "PK option_id; BK `(organization_id,plan_id,option_code)`; scoped Plan FK",
    },
    {
      name: "ben_eligibility",
      title: "Eligibility decision",
      description:
        "Eligibility decision. PK eligibility_id; BK `(organization_id,employment_id,plan_id,as_of_date)`; scoped Employment and Plan FKs",
      columns: [
        {
          name: "eligibility_id",
          domain: "ID",
          nullable: false,
          description: "Eligibility id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "employment_id",
          domain: "ID",
          nullable: false,
          description: "Employment id",
        },
        {
          name: "plan_id",
          domain: "ID",
          nullable: false,
          description: "Plan id",
        },
        {
          name: "as_of_date",
          domain: "DATE",
          nullable: false,
          description: "As of date",
        },
        {
          name: "is_eligible",
          domain: "BOOL",
          nullable: false,
          description: "Is eligible",
        },
        {
          name: "rule_reference",
          domain: "TEXT(500)",
          nullable: false,
          description: "Rule reference",
        },
        {
          name: "decision_reason",
          domain: "TEXT(2000)",
          nullable: false,
          description: "Decision reason",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["eligibility_id"],
        },
        {
          role: "business",
          members: [
            "organization_id",
            "employment_id",
            "plan_id",
            "as_of_date",
          ],
        },
      ],
      rules:
        "PK eligibility_id; BK `(organization_id,employment_id,plan_id,as_of_date)`; scoped Employment and Plan FKs",
    },
    {
      name: "ben_enrollment",
      title: "Enrollment",
      description:
        "Enrollment. PK enrollment_id; BK `(organization_id,election_number)`; scoped Employment and Option FKs",
      columns: [
        {
          name: "enrollment_id",
          domain: "ID",
          nullable: false,
          description: "Enrollment id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "employment_id",
          domain: "ID",
          nullable: false,
          description: "Employment id",
        },
        {
          name: "option_id",
          domain: "ID",
          nullable: false,
          description: "Option id",
        },
        {
          name: "election_number",
          domain: "CODE(64)",
          nullable: false,
          description: "Election number",
        },
        {
          name: "valid_from",
          domain: "DATE",
          nullable: false,
          description: "Valid from",
        },
        {
          name: "valid_to",
          domain: "DATE",
          nullable: true,
          description: "Valid to",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "elected/active/ended/cancelled",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["enrollment_id"],
        },
        {
          role: "business",
          members: ["organization_id", "election_number"],
        },
      ],
      rules:
        "PK enrollment_id; BK `(organization_id,election_number)`; scoped Employment and Option FKs",
    },
    {
      name: "ben_dependant",
      title: "Dependant relationship",
      description:
        "Dependant relationship. PK dependant_id; BK `(organization_id,employee_id,person_party_id,valid_from)`; scoped Employee and Person FKs",
      columns: [
        {
          name: "dependant_id",
          domain: "ID",
          nullable: false,
          description: "Dependant id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "employee_id",
          domain: "ID",
          nullable: false,
          description: "Employee id",
        },
        {
          name: "person_party_id",
          domain: "ID",
          nullable: false,
          description: "Person party id",
        },
        {
          name: "relationship_kind",
          domain: "CODE(32)",
          nullable: false,
          description: "Relationship kind",
        },
        {
          name: "valid_from",
          domain: "DATE",
          nullable: false,
          description: "Valid from",
        },
        {
          name: "valid_to",
          domain: "DATE",
          nullable: true,
          description: "Valid to",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["dependant_id"],
        },
        {
          role: "business",
          members: [
            "organization_id",
            "employee_id",
            "person_party_id",
            "valid_from",
          ],
        },
      ],
      rules:
        "PK dependant_id; BK `(organization_id,employee_id,person_party_id,valid_from)`; scoped Employee and Person FKs",
    },
    {
      name: "ben_covered_person",
      title: "Covered person",
      description:
        "Covered person. PK coverage_id; BK `(organization_id,enrollment_id,person_party_id,valid_from)`; scoped Enrollment/optional Dependant and Person FKs",
      columns: [
        {
          name: "coverage_id",
          domain: "ID",
          nullable: false,
          description: "Coverage id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "enrollment_id",
          domain: "ID",
          nullable: false,
          description: "Enrollment id",
        },
        {
          name: "person_party_id",
          domain: "ID",
          nullable: false,
          description: "Person party id",
        },
        {
          name: "dependant_id",
          domain: "ID",
          nullable: true,
          description: "Dependant id",
        },
        {
          name: "valid_from",
          domain: "DATE",
          nullable: false,
          description: "Valid from",
        },
        {
          name: "valid_to",
          domain: "DATE",
          nullable: true,
          description: "Valid to",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["coverage_id"],
        },
        {
          role: "business",
          members: [
            "organization_id",
            "enrollment_id",
            "person_party_id",
            "valid_from",
          ],
        },
      ],
      rules:
        "PK coverage_id; BK `(organization_id,enrollment_id,person_party_id,valid_from)`; scoped Enrollment/optional Dependant and Person FKs",
    },
    {
      name: "ben_contribution",
      title: "Benefit contribution",
      description:
        "Benefit contribution. PK contribution_id; BK `(organization_id,source_reference)`; scoped Enrollment FK; amount nonnegative unless explicit reversal profile selected",
      columns: [
        {
          name: "contribution_id",
          domain: "ID",
          nullable: false,
          description: "Contribution id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "enrollment_id",
          domain: "ID",
          nullable: false,
          description: "Enrollment id",
        },
        {
          name: "contribution_date",
          domain: "DATE",
          nullable: false,
          description: "Contribution date",
        },
        {
          name: "payer_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "employer/employee/other",
        },
        {
          name: "amount",
          domain: "MONEY",
          nullable: false,
          description: "Amount",
        },
        {
          name: "currency_code",
          domain: "CODE(3)",
          nullable: false,
          description: "Currency code",
        },
        {
          name: "source_reference",
          domain: "CODE(100)",
          nullable: false,
          description: "Source reference",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["contribution_id"],
        },
        {
          role: "business",
          members: ["organization_id", "source_reference"],
        },
      ],
      rules:
        "PK contribution_id; BK `(organization_id,source_reference)`; scoped Enrollment FK; amount nonnegative unless explicit reversal profile selected",
    },
  ],
  customer: [
    {
      name: "cus_account",
      title: "Customer account",
      description:
        "Customer account. PK customer_id; BK `(organization_id,customer_number)`; FKs Organization, Party and Party role",
      columns: [
        {
          name: "customer_id",
          domain: "ID",
          nullable: false,
          description: "Customer id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "seller",
        },
        {
          name: "party_id",
          domain: "ID",
          nullable: false,
          description: "customer",
        },
        {
          name: "party_role_id",
          domain: "ID",
          nullable: false,
          description: "Party role id",
        },
        {
          name: "customer_number",
          domain: "CODE(64)",
          nullable: false,
          description: "Customer number",
        },
        {
          name: "account_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "Account name",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "prospective/active/on_hold/closed",
        },
        {
          name: "default_currency_code",
          domain: "CODE(3)",
          nullable: true,
          description: "Default currency code",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["customer_id"],
        },
        {
          role: "business",
          members: ["organization_id", "customer_number"],
        },
      ],
      rules:
        "PK customer_id; BK `(organization_id,customer_number)`; FKs Organization, Party and Party role",
    },
    {
      name: "cus_contact",
      title: "Customer contact",
      description:
        "Customer contact. PK customer_contact_id; BK `(organization_id,customer_id,person_party_id,contact_role,valid_from)`; scoped Customer and Person FKs",
      columns: [
        {
          name: "customer_contact_id",
          domain: "ID",
          nullable: false,
          description: "Customer contact id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "customer_id",
          domain: "ID",
          nullable: false,
          description: "Customer id",
        },
        {
          name: "person_party_id",
          domain: "ID",
          nullable: false,
          description: "Person party id",
        },
        {
          name: "contact_role",
          domain: "CODE(32)",
          nullable: false,
          description: "Contact role",
        },
        {
          name: "valid_from",
          domain: "DATE",
          nullable: false,
          description: "Valid from",
        },
        {
          name: "valid_to",
          domain: "DATE",
          nullable: true,
          description: "Valid to",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["customer_contact_id"],
        },
        {
          role: "business",
          members: [
            "organization_id",
            "customer_id",
            "person_party_id",
            "contact_role",
            "valid_from",
          ],
        },
      ],
      rules:
        "PK customer_contact_id; BK `(organization_id,customer_id,person_party_id,contact_role,valid_from)`; scoped Customer and Person FKs",
    },
    {
      name: "cus_site",
      title: "Customer site",
      description:
        "Customer site. PK customer_site_id; BK `(organization_id,customer_id,site_code)`; scoped Customer and optional Party address FKs",
      columns: [
        {
          name: "customer_site_id",
          domain: "ID",
          nullable: false,
          description: "Customer site id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "customer_id",
          domain: "ID",
          nullable: false,
          description: "Customer id",
        },
        {
          name: "site_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Site code",
        },
        {
          name: "site_name",
          domain: "TEXT(150)",
          nullable: false,
          description: "Site name",
        },
        {
          name: "address_id",
          domain: "ID",
          nullable: true,
          description: "Address id",
        },
        {
          name: "site_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "billing/shipping/service/other",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "active/retired",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["customer_site_id"],
        },
        {
          role: "business",
          members: ["organization_id", "customer_id", "site_code"],
        },
      ],
      rules:
        "PK customer_site_id; BK `(organization_id,customer_id,site_code)`; scoped Customer and optional Party address FKs",
    },
    {
      name: "cus_account_policy",
      title: "Customer account policy",
      description:
        "Customer account policy. PK policy_id; BK `(organization_id,customer_id,valid_from)`; scoped Customer FK; credit limit requires currency; credit approval is external",
      columns: [
        {
          name: "policy_id",
          domain: "ID",
          nullable: false,
          description: "Policy id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "customer_id",
          domain: "ID",
          nullable: false,
          description: "Customer id",
        },
        {
          name: "valid_from",
          domain: "DATE",
          nullable: false,
          description: "Valid from",
        },
        {
          name: "valid_to",
          domain: "DATE",
          nullable: true,
          description: "Valid to",
        },
        {
          name: "payment_term_code",
          domain: "CODE(32)",
          nullable: true,
          description: "Payment term code",
        },
        {
          name: "credit_limit",
          domain: "MONEY",
          nullable: true,
          description: "Credit limit",
        },
        {
          name: "currency_code",
          domain: "CODE(3)",
          nullable: true,
          description: "Currency code",
        },
        {
          name: "policy_note",
          domain: "TEXT(2000)",
          nullable: true,
          description: "Policy note",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["policy_id"],
        },
        {
          role: "business",
          members: ["organization_id", "customer_id", "valid_from"],
        },
      ],
      rules:
        "PK policy_id; BK `(organization_id,customer_id,valid_from)`; scoped Customer FK; credit limit requires currency; credit approval is external",
    },
  ],
  supplier: [
    {
      name: "sup_account",
      title: "Supplier account",
      description:
        "Supplier account. PK supplier_id; BK `(organization_id,supplier_number)`; FKs Organization, Party and Party role",
      columns: [
        {
          name: "supplier_id",
          domain: "ID",
          nullable: false,
          description: "Supplier id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "buyer",
        },
        {
          name: "party_id",
          domain: "ID",
          nullable: false,
          description: "supplier",
        },
        {
          name: "party_role_id",
          domain: "ID",
          nullable: false,
          description: "Party role id",
        },
        {
          name: "supplier_number",
          domain: "CODE(64)",
          nullable: false,
          description: "Supplier number",
        },
        {
          name: "account_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "Account name",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "prospective/active/on_hold/closed",
        },
        {
          name: "default_currency_code",
          domain: "CODE(3)",
          nullable: true,
          description: "Default currency code",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["supplier_id"],
        },
        {
          role: "business",
          members: ["organization_id", "supplier_number"],
        },
      ],
      rules:
        "PK supplier_id; BK `(organization_id,supplier_number)`; FKs Organization, Party and Party role",
    },
    {
      name: "sup_contact",
      title: "Supplier contact",
      description:
        "Supplier contact. PK supplier_contact_id; BK `(organization_id,supplier_id,person_party_id,contact_role,valid_from)`; scoped Supplier and Person FKs",
      columns: [
        {
          name: "supplier_contact_id",
          domain: "ID",
          nullable: false,
          description: "Supplier contact id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "supplier_id",
          domain: "ID",
          nullable: false,
          description: "Supplier id",
        },
        {
          name: "person_party_id",
          domain: "ID",
          nullable: false,
          description: "Person party id",
        },
        {
          name: "contact_role",
          domain: "CODE(32)",
          nullable: false,
          description: "Contact role",
        },
        {
          name: "valid_from",
          domain: "DATE",
          nullable: false,
          description: "Valid from",
        },
        {
          name: "valid_to",
          domain: "DATE",
          nullable: true,
          description: "Valid to",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["supplier_contact_id"],
        },
        {
          role: "business",
          members: [
            "organization_id",
            "supplier_id",
            "person_party_id",
            "contact_role",
            "valid_from",
          ],
        },
      ],
      rules:
        "PK supplier_contact_id; BK `(organization_id,supplier_id,person_party_id,contact_role,valid_from)`; scoped Supplier and Person FKs",
    },
    {
      name: "sup_site",
      title: "Supplier site",
      description:
        "Supplier site. PK supplier_site_id; BK `(organization_id,supplier_id,site_code)`; scoped Supplier and optional Party address FKs",
      columns: [
        {
          name: "supplier_site_id",
          domain: "ID",
          nullable: false,
          description: "Supplier site id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "supplier_id",
          domain: "ID",
          nullable: false,
          description: "Supplier id",
        },
        {
          name: "site_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Site code",
        },
        {
          name: "site_name",
          domain: "TEXT(150)",
          nullable: false,
          description: "Site name",
        },
        {
          name: "address_id",
          domain: "ID",
          nullable: true,
          description: "Address id",
        },
        {
          name: "site_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "ordering/remittance/service/other",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "active/retired",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["supplier_site_id"],
        },
        {
          role: "business",
          members: ["organization_id", "supplier_id", "site_code"],
        },
      ],
      rules:
        "PK supplier_site_id; BK `(organization_id,supplier_id,site_code)`; scoped Supplier and optional Party address FKs",
    },
    {
      name: "sup_account_policy",
      title: "Supplier account policy",
      description:
        "Supplier account policy. PK policy_id; BK `(organization_id,supplier_id,valid_from)`; scoped Supplier FK",
      columns: [
        {
          name: "policy_id",
          domain: "ID",
          nullable: false,
          description: "Policy id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "supplier_id",
          domain: "ID",
          nullable: false,
          description: "Supplier id",
        },
        {
          name: "valid_from",
          domain: "DATE",
          nullable: false,
          description: "Valid from",
        },
        {
          name: "valid_to",
          domain: "DATE",
          nullable: true,
          description: "Valid to",
        },
        {
          name: "payment_term_code",
          domain: "CODE(32)",
          nullable: true,
          description: "Payment term code",
        },
        {
          name: "preferred",
          domain: "BOOL",
          nullable: false,
          description: "Preferred",
        },
        {
          name: "policy_note",
          domain: "TEXT(2000)",
          nullable: true,
          description: "Policy note",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["policy_id"],
        },
        {
          role: "business",
          members: ["organization_id", "supplier_id", "valid_from"],
        },
      ],
      rules:
        "PK policy_id; BK `(organization_id,supplier_id,valid_from)`; scoped Supplier FK",
    },
  ],
  inventory: [
    {
      name: "inv_item",
      title: "Inventory item",
      description:
        "Inventory item. PK item_id; BK `(organization_id,item_code)`; unit vocabulary required",
      columns: [
        {
          name: "item_id",
          domain: "ID",
          nullable: false,
          description: "Item id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "item_code",
          domain: "CODE(64)",
          nullable: false,
          description: "Item code",
        },
        {
          name: "item_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "Item name",
        },
        {
          name: "base_unit_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Base unit code",
        },
        {
          name: "item_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "stock/service/nonstock",
        },
        {
          name: "lot_controlled",
          domain: "BOOL",
          nullable: false,
          description: "Lot controlled",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "active/retired",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["item_id"],
        },
        {
          role: "business",
          members: ["organization_id", "item_code"],
        },
      ],
      rules:
        "PK item_id; BK `(organization_id,item_code)`; unit vocabulary required",
    },
    {
      name: "inv_site",
      title: "Inventory site",
      description:
        "Inventory site. PK site_id; BK `(organization_id,site_code)`; optional scoped org_location FK",
      columns: [
        {
          name: "site_id",
          domain: "ID",
          nullable: false,
          description: "Site id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "site_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Site code",
        },
        {
          name: "site_name",
          domain: "TEXT(150)",
          nullable: false,
          description: "Site name",
        },
        {
          name: "location_id",
          domain: "ID",
          nullable: true,
          description: "Location id",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["site_id"],
        },
        {
          role: "business",
          members: ["organization_id", "site_code"],
        },
      ],
      rules:
        "PK site_id; BK `(organization_id,site_code)`; optional scoped org_location FK",
    },
    {
      name: "inv_location",
      title: "Stock location",
      description:
        "Stock location. PK stock_location_id; BK `(organization_id,site_id,location_code)`; scoped Site FK",
      columns: [
        {
          name: "stock_location_id",
          domain: "ID",
          nullable: false,
          description: "Stock location id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "site_id",
          domain: "ID",
          nullable: false,
          description: "Site id",
        },
        {
          name: "location_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Location code",
        },
        {
          name: "location_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "storage/transit/quarantine/virtual",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["stock_location_id"],
        },
        {
          role: "business",
          members: ["organization_id", "site_id", "location_code"],
        },
      ],
      rules:
        "PK stock_location_id; BK `(organization_id,site_id,location_code)`; scoped Site FK",
    },
    {
      name: "inv_lot",
      title: "Item lot",
      description:
        "Item lot. PK lot_id; BK `(organization_id,item_id,lot_number)`; scoped Item FK",
      columns: [
        {
          name: "lot_id",
          domain: "ID",
          nullable: false,
          description: "Lot id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "item_id",
          domain: "ID",
          nullable: false,
          description: "Item id",
        },
        {
          name: "lot_number",
          domain: "CODE(64)",
          nullable: false,
          description: "Lot number",
        },
        {
          name: "manufactured_on",
          domain: "DATE",
          nullable: true,
          description: "Manufactured on",
        },
        {
          name: "expires_on",
          domain: "DATE",
          nullable: true,
          description: "Expires on",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["lot_id"],
        },
        {
          role: "business",
          members: ["organization_id", "item_id", "lot_number"],
        },
      ],
      rules:
        "PK lot_id; BK `(organization_id,item_id,lot_number)`; scoped Item FK",
    },
    {
      name: "inv_movement",
      title: "Movement header",
      description:
        "Movement header. PK movement_id; BK `(organization_id,movement_number)`; optional scoped self FK",
      columns: [
        {
          name: "movement_id",
          domain: "ID",
          nullable: false,
          description: "Movement id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "movement_number",
          domain: "CODE(64)",
          nullable: false,
          description: "Movement number",
        },
        {
          name: "occurred_at",
          domain: "INSTANT",
          nullable: false,
          description: "Occurred at",
        },
        {
          name: "movement_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "receipt/issue/transfer/adjustment/reversal",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "draft/posted/reversed",
        },
        {
          name: "source_reference",
          domain: "CODE(100)",
          nullable: true,
          description: "Source reference",
        },
        {
          name: "reverses_movement_id",
          domain: "ID",
          nullable: true,
          description: "Reverses movement id",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["movement_id"],
        },
        {
          role: "business",
          members: ["organization_id", "movement_number"],
        },
      ],
      rules:
        "PK movement_id; BK `(organization_id,movement_number)`; optional scoped self FK",
    },
    {
      name: "inv_movement_line",
      title: "Movement line",
      description:
        "Movement line. PK movement_line_id; BK `(organization_id,movement_id,line_number)`; scoped Movement, Item, optional Lot and Location FKs",
      columns: [
        {
          name: "movement_line_id",
          domain: "ID",
          nullable: false,
          description: "Movement line id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "movement_id",
          domain: "ID",
          nullable: false,
          description: "Movement id",
        },
        {
          name: "line_number",
          domain: "INTEGER",
          nullable: false,
          description: "Line number",
        },
        {
          name: "item_id",
          domain: "ID",
          nullable: false,
          description: "Item id",
        },
        {
          name: "lot_id",
          domain: "ID",
          nullable: true,
          description: "Lot id",
        },
        {
          name: "from_location_id",
          domain: "ID",
          nullable: true,
          description: "From location id",
        },
        {
          name: "to_location_id",
          domain: "ID",
          nullable: true,
          description: "To location id",
        },
        {
          name: "quantity",
          domain: "QTY",
          nullable: false,
          description: "Quantity",
        },
        {
          name: "unit_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Unit code",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["movement_line_id"],
        },
        {
          role: "business",
          members: ["organization_id", "movement_id", "line_number"],
        },
      ],
      rules:
        "PK movement_line_id; BK `(organization_id,movement_id,line_number)`; scoped Movement, Item, optional Lot and Location FKs",
    },
    {
      name: "inv_reservation",
      title: "Reservation",
      description:
        "Reservation. PK reservation_id; BK `(organization_id,reservation_reference)`; scoped Item/optional Location/Lot FKs",
      columns: [
        {
          name: "reservation_id",
          domain: "ID",
          nullable: false,
          description: "Reservation id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "item_id",
          domain: "ID",
          nullable: false,
          description: "Item id",
        },
        {
          name: "stock_location_id",
          domain: "ID",
          nullable: true,
          description: "Stock location id",
        },
        {
          name: "lot_id",
          domain: "ID",
          nullable: true,
          description: "Lot id",
        },
        {
          name: "reservation_reference",
          domain: "CODE(100)",
          nullable: false,
          description: "Reservation reference",
        },
        {
          name: "quantity",
          domain: "QTY",
          nullable: false,
          description: "Quantity",
        },
        {
          name: "unit_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Unit code",
        },
        {
          name: "valid_from",
          domain: "INSTANT",
          nullable: false,
          description: "Valid from",
        },
        {
          name: "valid_to",
          domain: "INSTANT",
          nullable: true,
          description: "Valid to",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "active/consumed/released/expired",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["reservation_id"],
        },
        {
          role: "business",
          members: ["organization_id", "reservation_reference"],
        },
      ],
      rules:
        "PK reservation_id; BK `(organization_id,reservation_reference)`; scoped Item/optional Location/Lot FKs",
    },
    {
      name: "inv_stock_position",
      title: "Stock position",
      description:
        "Stock position. Derived read model; declared grain includes lot-null policy and instant; no invented stored mutable balance",
      columns: [
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description:
            "Derived organization id; Derived read model; declared grain includes lot-null policy and instant; no invented stored mutable balance",
        },
        {
          name: "item_id",
          domain: "ID",
          nullable: false,
          description:
            "Derived item id; Derived read model; declared grain includes lot-null policy and instant; no invented stored mutable balance",
        },
        {
          name: "stock_location_id",
          domain: "ID",
          nullable: false,
          description:
            "Derived stock location id; Derived read model; declared grain includes lot-null policy and instant; no invented stored mutable balance",
        },
        {
          name: "lot_id",
          domain: "ID",
          nullable: true,
          description:
            "Derived lot id; Derived read model; declared grain includes lot-null policy and instant; no invented stored mutable balance",
        },
        {
          name: "unit_code",
          domain: "TEXT(100)",
          nullable: false,
          description:
            "Derived unit code; Derived read model; declared grain includes lot-null policy and instant; no invented stored mutable balance",
        },
        {
          name: "on_hand_quantity",
          domain: "QTY",
          nullable: false,
          description:
            "Derived on hand quantity; Derived read model; declared grain includes lot-null policy and instant; no invented stored mutable balance",
        },
        {
          name: "reserved_quantity",
          domain: "QTY",
          nullable: false,
          description:
            "Derived reserved quantity; Derived read model; declared grain includes lot-null policy and instant; no invented stored mutable balance",
        },
        {
          name: "available_quantity",
          domain: "QTY",
          nullable: false,
          description:
            "Derived available quantity; Derived read model; declared grain includes lot-null policy and instant; no invented stored mutable balance",
        },
        {
          name: "as_of_instant",
          domain: "INSTANT",
          nullable: false,
          description:
            "Derived as of instant; Derived read model; declared grain includes lot-null policy and instant; no invented stored mutable balance",
        },
      ],
      keys: [],
      rules:
        "Derived read model; declared grain includes lot-null policy and instant; no invented stored mutable balance",
      derived: true,
    },
  ],
  sales: [
    {
      name: "sal_order",
      title: "Sales order",
      description:
        "Sales order. PK order_id; BK `(organization_id,order_number)`; scoped Customer FK",
      columns: [
        {
          name: "order_id",
          domain: "ID",
          nullable: false,
          description: "Order id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "customer_id",
          domain: "ID",
          nullable: false,
          description: "Customer id",
        },
        {
          name: "order_number",
          domain: "CODE(64)",
          nullable: false,
          description: "Order number",
        },
        {
          name: "ordered_on",
          domain: "DATE",
          nullable: false,
          description: "Ordered on",
        },
        {
          name: "currency_code",
          domain: "CODE(3)",
          nullable: false,
          description: "Currency code",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "draft/confirmed/part_fulfilled/fulfilled/cancelled",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["order_id"],
        },
        {
          role: "business",
          members: ["organization_id", "order_number"],
        },
      ],
      rules:
        "PK order_id; BK `(organization_id,order_number)`; scoped Customer FK",
    },
    {
      name: "sal_order_line",
      title: "Sales order line",
      description:
        "Sales order line. PK order_line_id; BK `(organization_id,order_id,line_number)`; scoped Order/Item FKs; currency inherited explicitly from order",
      columns: [
        {
          name: "order_line_id",
          domain: "ID",
          nullable: false,
          description: "Order line id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "order_id",
          domain: "ID",
          nullable: false,
          description: "Order id",
        },
        {
          name: "line_number",
          domain: "INTEGER",
          nullable: false,
          description: "Line number",
        },
        {
          name: "item_id",
          domain: "ID",
          nullable: false,
          description: "Item id",
        },
        {
          name: "description",
          domain: "TEXT(500)",
          nullable: false,
          description: "Description",
        },
        {
          name: "ordered_quantity",
          domain: "QTY",
          nullable: false,
          description: "Ordered quantity",
        },
        {
          name: "unit_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Unit code",
        },
        {
          name: "unit_price",
          domain: "MONEY",
          nullable: false,
          description: "Unit price",
        },
        {
          name: "discount_amount",
          domain: "MONEY",
          nullable: false,
          description: "Discount amount",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["order_line_id"],
        },
        {
          role: "business",
          members: ["organization_id", "order_id", "line_number"],
        },
      ],
      rules:
        "PK order_line_id; BK `(organization_id,order_id,line_number)`; scoped Order/Item FKs; currency inherited explicitly from order",
    },
    {
      name: "sal_order_address",
      title: "Order address snapshot",
      description:
        "Order address snapshot. PK order_address_id; BK `(organization_id,order_id,address_role)`; scoped Order/optional Customer site FKs; site must belong to order customer",
      columns: [
        {
          name: "order_address_id",
          domain: "ID",
          nullable: false,
          description: "Order address id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "order_id",
          domain: "ID",
          nullable: false,
          description: "Order id",
        },
        {
          name: "address_role",
          domain: "CODE(16)",
          nullable: false,
          description: "bill_to/ship_to",
        },
        {
          name: "customer_site_id",
          domain: "ID",
          nullable: true,
          description: "Customer site id",
        },
        {
          name: "address_text",
          domain: "TEXT(2000)",
          nullable: false,
          description: "Address text",
        },
        {
          name: "captured_at",
          domain: "INSTANT",
          nullable: false,
          description: "Captured at",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["order_address_id"],
        },
        {
          role: "business",
          members: ["organization_id", "order_id", "address_role"],
        },
      ],
      rules:
        "PK order_address_id; BK `(organization_id,order_id,address_role)`; scoped Order/optional Customer site FKs; site must belong to order customer",
    },
    {
      name: "sal_allocation",
      title: "Fulfillment allocation",
      description:
        "Fulfillment allocation. PK allocation_id; UQ `(organization_id,order_line_id,reservation_id)`; scoped Order line and Inventory reservation FKs",
      columns: [
        {
          name: "allocation_id",
          domain: "ID",
          nullable: false,
          description: "Allocation id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "order_line_id",
          domain: "ID",
          nullable: false,
          description: "Order line id",
        },
        {
          name: "reservation_id",
          domain: "ID",
          nullable: false,
          description: "Reservation id",
        },
        {
          name: "allocated_quantity",
          domain: "QTY",
          nullable: false,
          description: "Allocated quantity",
        },
        {
          name: "unit_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Unit code",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "active/fulfilled/released",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["allocation_id"],
        },
        {
          role: "unique",
          members: ["organization_id", "order_line_id", "reservation_id"],
        },
      ],
      rules:
        "PK allocation_id; UQ `(organization_id,order_line_id,reservation_id)`; scoped Order line and Inventory reservation FKs",
    },
    {
      name: "sal_shipment",
      title: "Shipment",
      description:
        "Shipment. PK shipment_id; BK `(organization_id,shipment_number)`; scoped Order FK",
      columns: [
        {
          name: "shipment_id",
          domain: "ID",
          nullable: false,
          description: "Shipment id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "order_id",
          domain: "ID",
          nullable: false,
          description: "Order id",
        },
        {
          name: "shipment_number",
          domain: "CODE(64)",
          nullable: false,
          description: "Shipment number",
        },
        {
          name: "shipped_at",
          domain: "INSTANT",
          nullable: true,
          description: "Shipped at",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "draft/dispatched/delivered/cancelled",
        },
        {
          name: "tracking_reference",
          domain: "TEXT(200)",
          nullable: true,
          description: "Tracking reference",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["shipment_id"],
        },
        {
          role: "business",
          members: ["organization_id", "shipment_number"],
        },
      ],
      rules:
        "PK shipment_id; BK `(organization_id,shipment_number)`; scoped Order FK",
    },
    {
      name: "sal_shipment_line",
      title: "Shipment line",
      description:
        "Shipment line. PK shipment_line_id; BK `(organization_id,shipment_id,line_number)`; scoped Shipment/Order line/optional Inventory movement line FKs",
      columns: [
        {
          name: "shipment_line_id",
          domain: "ID",
          nullable: false,
          description: "Shipment line id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "shipment_id",
          domain: "ID",
          nullable: false,
          description: "Shipment id",
        },
        {
          name: "order_line_id",
          domain: "ID",
          nullable: false,
          description: "Order line id",
        },
        {
          name: "line_number",
          domain: "INTEGER",
          nullable: false,
          description: "Line number",
        },
        {
          name: "shipped_quantity",
          domain: "QTY",
          nullable: false,
          description: "Shipped quantity",
        },
        {
          name: "unit_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Unit code",
        },
        {
          name: "movement_line_id",
          domain: "ID",
          nullable: true,
          description: "Movement line id",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["shipment_line_id"],
        },
        {
          role: "business",
          members: ["organization_id", "shipment_id", "line_number"],
        },
      ],
      rules:
        "PK shipment_line_id; BK `(organization_id,shipment_id,line_number)`; scoped Shipment/Order line/optional Inventory movement line FKs",
    },
    {
      name: "sal_return",
      title: "Sales return",
      description:
        "Sales return. PK return_id; BK `(organization_id,return_number)`; scoped Order FK",
      columns: [
        {
          name: "return_id",
          domain: "ID",
          nullable: false,
          description: "Return id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "order_id",
          domain: "ID",
          nullable: false,
          description: "Order id",
        },
        {
          name: "return_number",
          domain: "CODE(64)",
          nullable: false,
          description: "Return number",
        },
        {
          name: "requested_on",
          domain: "DATE",
          nullable: false,
          description: "Requested on",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "requested/authorized/received/rejected/closed",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["return_id"],
        },
        {
          role: "business",
          members: ["organization_id", "return_number"],
        },
      ],
      rules:
        "PK return_id; BK `(organization_id,return_number)`; scoped Order FK",
    },
    {
      name: "sal_return_line",
      title: "Sales return line",
      description:
        "Sales return line. PK return_line_id; BK `(organization_id,return_id,line_number)`; scoped Return/Shipment line/optional Inventory movement line FKs",
      columns: [
        {
          name: "return_line_id",
          domain: "ID",
          nullable: false,
          description: "Return line id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "return_id",
          domain: "ID",
          nullable: false,
          description: "Return id",
        },
        {
          name: "shipment_line_id",
          domain: "ID",
          nullable: false,
          description: "Shipment line id",
        },
        {
          name: "line_number",
          domain: "INTEGER",
          nullable: false,
          description: "Line number",
        },
        {
          name: "return_quantity",
          domain: "QTY",
          nullable: false,
          description: "Return quantity",
        },
        {
          name: "unit_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Unit code",
        },
        {
          name: "reason_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Reason code",
        },
        {
          name: "movement_line_id",
          domain: "ID",
          nullable: true,
          description: "Movement line id",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["return_line_id"],
        },
        {
          role: "business",
          members: ["organization_id", "return_id", "line_number"],
        },
      ],
      rules:
        "PK return_line_id; BK `(organization_id,return_id,line_number)`; scoped Return/Shipment line/optional Inventory movement line FKs",
    },
  ],
  finance: [
    {
      name: "fin_chart",
      title: "Chart of accounts",
      description:
        "Chart of accounts. PK chart_id; BK `(organization_id,chart_code)`; FK Organization",
      columns: [
        {
          name: "chart_id",
          domain: "ID",
          nullable: false,
          description: "Chart id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "chart_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Chart code",
        },
        {
          name: "chart_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "Chart name",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "draft/active/retired",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["chart_id"],
        },
        {
          role: "business",
          members: ["organization_id", "chart_code"],
        },
      ],
      rules: "PK chart_id; BK `(organization_id,chart_code)`; FK Organization",
    },
    {
      name: "fin_account",
      title: "General ledger account",
      description:
        "General ledger account. PK account_id; BK `(organization_id,chart_id,account_code)`; scoped Chart FK; UQ `(chart_id,account_id)` for ledger-chart checking",
      columns: [
        {
          name: "account_id",
          domain: "ID",
          nullable: false,
          description: "Account id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "chart_id",
          domain: "ID",
          nullable: false,
          description: "Chart id",
        },
        {
          name: "account_code",
          domain: "CODE(64)",
          nullable: false,
          description: "Account code",
        },
        {
          name: "account_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "Account name",
        },
        {
          name: "account_class",
          domain: "CODE(16)",
          nullable: false,
          description: "asset/liability/equity/income/expense",
        },
        {
          name: "normal_side",
          domain: "CODE(6)",
          nullable: false,
          description: "debit/credit",
        },
        {
          name: "allow_direct_posting",
          domain: "BOOL",
          nullable: false,
          description: "Allow direct posting",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "active/blocked/retired",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["account_id"],
        },
        {
          role: "business",
          members: ["organization_id", "chart_id", "account_code"],
        },
        {
          role: "unique",
          members: ["chart_id", "account_id"],
        },
      ],
      rules:
        "PK account_id; BK `(organization_id,chart_id,account_code)`; scoped Chart FK; UQ `(chart_id,account_id)` for ledger-chart checking",
    },
    {
      name: "fin_ledger",
      title: "Ledger",
      description:
        "Ledger. PK ledger_id; BK `(organization_id,ledger_code)`; scoped Chart FK",
      columns: [
        {
          name: "ledger_id",
          domain: "ID",
          nullable: false,
          description: "Ledger id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "chart_id",
          domain: "ID",
          nullable: false,
          description: "Chart id",
        },
        {
          name: "ledger_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Ledger code",
        },
        {
          name: "ledger_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "Ledger name",
        },
        {
          name: "functional_currency_code",
          domain: "CODE(3)",
          nullable: false,
          description: "Functional currency code",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "active/closed",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["ledger_id"],
        },
        {
          role: "business",
          members: ["organization_id", "ledger_code"],
        },
      ],
      rules:
        "PK ledger_id; BK `(organization_id,ledger_code)`; scoped Chart FK",
    },
    {
      name: "fin_period",
      title: "Accounting period",
      description:
        "Accounting period. PK period_id; BK `(ledger_id,period_code)`; FK Ledger; end exclusive; adjustment periods explicitly distinguished",
      columns: [
        {
          name: "period_id",
          domain: "ID",
          nullable: false,
          description: "Period id",
        },
        {
          name: "ledger_id",
          domain: "ID",
          nullable: false,
          description: "Ledger id",
        },
        {
          name: "period_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Period code",
        },
        {
          name: "start_date",
          domain: "DATE",
          nullable: false,
          description: "Start date",
        },
        {
          name: "end_date",
          domain: "DATE",
          nullable: false,
          description: "End date",
        },
        {
          name: "period_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "ordinary/adjustment",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "planned/open/closed",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["period_id"],
        },
        {
          role: "business",
          members: ["ledger_id", "period_code"],
        },
      ],
      rules:
        "PK period_id; BK `(ledger_id,period_code)`; FK Ledger; end exclusive; adjustment periods explicitly distinguished",
    },
    {
      name: "fin_journal",
      title: "Journal",
      description:
        "Journal. PK journal_id; BK `(ledger_id,journal_number)`; scoped Period and optional self FK; conditional source-document idempotency rule when both source fields supplied",
      columns: [
        {
          name: "journal_id",
          domain: "ID",
          nullable: false,
          description: "Journal id",
        },
        {
          name: "ledger_id",
          domain: "ID",
          nullable: false,
          description: "Ledger id",
        },
        {
          name: "period_id",
          domain: "ID",
          nullable: false,
          description: "Period id",
        },
        {
          name: "journal_number",
          domain: "CODE(64)",
          nullable: false,
          description: "Journal number",
        },
        {
          name: "posting_date",
          domain: "DATE",
          nullable: false,
          description: "Posting date",
        },
        {
          name: "transaction_currency_code",
          domain: "CODE(3)",
          nullable: false,
          description: "Transaction currency code",
        },
        {
          name: "description",
          domain: "TEXT(1000)",
          nullable: false,
          description: "Description",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "draft/posted/reversed",
        },
        {
          name: "posted_at",
          domain: "INSTANT",
          nullable: true,
          description: "Posted at",
        },
        {
          name: "source_system",
          domain: "CODE(64)",
          nullable: true,
          description: "Source system",
        },
        {
          name: "source_document_key",
          domain: "CODE(200)",
          nullable: true,
          description: "Source document key",
        },
        {
          name: "reverses_journal_id",
          domain: "ID",
          nullable: true,
          description: "Reverses journal id",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["journal_id"],
        },
        {
          role: "business",
          members: ["ledger_id", "journal_number"],
        },
      ],
      rules:
        "PK journal_id; BK `(ledger_id,journal_number)`; scoped Period and optional self FK; conditional source-document idempotency rule when both source fields supplied",
    },
    {
      name: "fin_journal_line",
      title: "Journal line",
      description:
        "Journal line. PK journal_line_id; BK `(ledger_id,journal_id,line_number)`; scoped Journal and subsidiary account FKs; Account must be from ledger chart via explicit validator or materialized compound reference",
      columns: [
        {
          name: "journal_line_id",
          domain: "ID",
          nullable: false,
          description: "Journal line id",
        },
        {
          name: "ledger_id",
          domain: "ID",
          nullable: false,
          description: "Ledger id",
        },
        {
          name: "journal_id",
          domain: "ID",
          nullable: false,
          description: "Journal id",
        },
        {
          name: "line_number",
          domain: "INTEGER",
          nullable: false,
          description: "Line number",
        },
        {
          name: "account_id",
          domain: "ID",
          nullable: false,
          description: "Account id",
        },
        {
          name: "debit_amount",
          domain: "MONEY",
          nullable: false,
          description: "Debit amount",
        },
        {
          name: "credit_amount",
          domain: "MONEY",
          nullable: false,
          description: "Credit amount",
        },
        {
          name: "functional_debit",
          domain: "MONEY",
          nullable: false,
          description: "Functional debit",
        },
        {
          name: "functional_credit",
          domain: "MONEY",
          nullable: false,
          description: "Functional credit",
        },
        {
          name: "rate_value",
          domain: "DECIMAL(28,12)",
          nullable: true,
          description: "Rate value",
        },
        {
          name: "rate_date",
          domain: "DATE",
          nullable: true,
          description: "Rate date",
        },
        {
          name: "rate_source",
          domain: "TEXT(200)",
          nullable: true,
          description: "Rate source",
        },
        {
          name: "description",
          domain: "TEXT(1000)",
          nullable: true,
          description: "Description",
        },
        {
          name: "debtor_account_id",
          domain: "ID",
          nullable: true,
          description: "Debtor account id",
        },
        {
          name: "creditor_account_id",
          domain: "ID",
          nullable: true,
          description: "Creditor account id",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["journal_line_id"],
        },
        {
          role: "business",
          members: ["ledger_id", "journal_id", "line_number"],
        },
      ],
      rules:
        "PK journal_line_id; BK `(ledger_id,journal_id,line_number)`; scoped Journal and subsidiary account FKs; Account must be from ledger chart via explicit validator or materialized compound reference",
    },
    {
      name: "fin_gl_posting",
      title: "General ledger posting",
      description:
        "General ledger posting. Read-only derivation of accepted posted journal lines, including originals later marked reversed; declared unique output identity journal_line_id; no separately mutable balance",
      columns: [
        {
          name: "journal_line_id",
          domain: "ID",
          nullable: false,
          description:
            "Derived journal line id; Read-only derivation of accepted posted journal lines, including originals later marked reversed; declared unique output identity journal_line_id; no separately mutable balance",
        },
        {
          name: "ledger_id",
          domain: "ID",
          nullable: false,
          description:
            "Derived ledger id; Read-only derivation of accepted posted journal lines, including originals later marked reversed; declared unique output identity journal_line_id; no separately mutable balance",
        },
        {
          name: "period_id",
          domain: "ID",
          nullable: false,
          description:
            "Derived period id; Read-only derivation of accepted posted journal lines, including originals later marked reversed; declared unique output identity journal_line_id; no separately mutable balance",
        },
        {
          name: "posting_date",
          domain: "DATE",
          nullable: false,
          description:
            "Derived posting date; Read-only derivation of accepted posted journal lines, including originals later marked reversed; declared unique output identity journal_line_id; no separately mutable balance",
        },
        {
          name: "account_id",
          domain: "ID",
          nullable: false,
          description:
            "Derived account id; Read-only derivation of accepted posted journal lines, including originals later marked reversed; declared unique output identity journal_line_id; no separately mutable balance",
        },
        {
          name: "functional_debit",
          domain: "MONEY",
          nullable: false,
          description:
            "Derived functional debit; Read-only derivation of accepted posted journal lines, including originals later marked reversed; declared unique output identity journal_line_id; no separately mutable balance",
        },
        {
          name: "functional_credit",
          domain: "MONEY",
          nullable: false,
          description:
            "Derived functional credit; Read-only derivation of accepted posted journal lines, including originals later marked reversed; declared unique output identity journal_line_id; no separately mutable balance",
        },
        {
          name: "transaction_currency_code",
          domain: "TEXT(100)",
          nullable: false,
          description:
            "Derived transaction currency code; Read-only derivation of accepted posted journal lines, including originals later marked reversed; declared unique output identity journal_line_id; no separately mutable balance",
        },
      ],
      keys: [],
      rules:
        "Read-only derivation of accepted posted journal lines, including originals later marked reversed; declared unique output identity journal_line_id; no separately mutable balance",
      derived: true,
    },
    {
      name: "fin_debtor_account",
      title: "Debtor account",
      description:
        "Debtor account. PK debtor_account_id; BK `(ledger_id,customer_id,currency_code)`; scoped Customer FK; Ledger organization must agree; control Account belongs to ledger chart",
      columns: [
        {
          name: "debtor_account_id",
          domain: "ID",
          nullable: false,
          description: "Debtor account id",
        },
        {
          name: "ledger_id",
          domain: "ID",
          nullable: false,
          description: "Ledger id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "customer_id",
          domain: "ID",
          nullable: false,
          description: "Customer id",
        },
        {
          name: "currency_code",
          domain: "CODE(3)",
          nullable: false,
          description: "Currency code",
        },
        {
          name: "control_account_id",
          domain: "ID",
          nullable: false,
          description: "Control account id",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "active/blocked/closed",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["debtor_account_id"],
        },
        {
          role: "business",
          members: ["ledger_id", "customer_id", "currency_code"],
        },
      ],
      rules:
        "PK debtor_account_id; BK `(ledger_id,customer_id,currency_code)`; scoped Customer FK; Ledger organization must agree; control Account belongs to ledger chart",
    },
    {
      name: "fin_creditor_account",
      title: "Creditor account",
      description:
        "Creditor account. PK creditor_account_id; BK `(ledger_id,supplier_id,currency_code)`; scoped Supplier FK; Ledger organization must agree; control Account belongs to ledger chart",
      columns: [
        {
          name: "creditor_account_id",
          domain: "ID",
          nullable: false,
          description: "Creditor account id",
        },
        {
          name: "ledger_id",
          domain: "ID",
          nullable: false,
          description: "Ledger id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "supplier_id",
          domain: "ID",
          nullable: false,
          description: "Supplier id",
        },
        {
          name: "currency_code",
          domain: "CODE(3)",
          nullable: false,
          description: "Currency code",
        },
        {
          name: "control_account_id",
          domain: "ID",
          nullable: false,
          description: "Control account id",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "active/blocked/closed",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["creditor_account_id"],
        },
        {
          role: "business",
          members: ["ledger_id", "supplier_id", "currency_code"],
        },
      ],
      rules:
        "PK creditor_account_id; BK `(ledger_id,supplier_id,currency_code)`; scoped Supplier FK; Ledger organization must agree; control Account belongs to ledger chart",
    },
    {
      name: "fin_receivable_item",
      title: "Receivable item",
      description:
        "Receivable item. PK receivable_item_id; BK `(ledger_id,debtor_account_id,document_number,document_kind)`; scoped Debtor and Journal line FKs; UQ `(ledger_id,journal_line_id)`; posted line is the originating control posting",
      columns: [
        {
          name: "receivable_item_id",
          domain: "ID",
          nullable: false,
          description: "Receivable item id",
        },
        {
          name: "ledger_id",
          domain: "ID",
          nullable: false,
          description: "Ledger id",
        },
        {
          name: "debtor_account_id",
          domain: "ID",
          nullable: false,
          description: "Debtor account id",
        },
        {
          name: "document_number",
          domain: "CODE(100)",
          nullable: false,
          description: "Document number",
        },
        {
          name: "document_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "invoice/credit_note/opening",
        },
        {
          name: "document_date",
          domain: "DATE",
          nullable: false,
          description: "Document date",
        },
        {
          name: "due_date",
          domain: "DATE",
          nullable: false,
          description: "Due date",
        },
        {
          name: "original_amount",
          domain: "MONEY",
          nullable: false,
          description: "Original amount",
        },
        {
          name: "journal_line_id",
          domain: "ID",
          nullable: false,
          description: "Journal line id",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["receivable_item_id"],
        },
        {
          role: "business",
          members: [
            "ledger_id",
            "debtor_account_id",
            "document_number",
            "document_kind",
          ],
        },
        {
          role: "unique",
          members: ["ledger_id", "journal_line_id"],
        },
      ],
      rules:
        "PK receivable_item_id; BK `(ledger_id,debtor_account_id,document_number,document_kind)`; scoped Debtor and Journal line FKs; UQ `(ledger_id,journal_line_id)`; posted line is the originating control posting",
    },
    {
      name: "fin_payable_item",
      title: "Payable item",
      description:
        "Payable item. PK payable_item_id; BK `(ledger_id,creditor_account_id,document_number,document_kind)`; scoped Creditor and Journal line FKs; UQ `(ledger_id,journal_line_id)`",
      columns: [
        {
          name: "payable_item_id",
          domain: "ID",
          nullable: false,
          description: "Payable item id",
        },
        {
          name: "ledger_id",
          domain: "ID",
          nullable: false,
          description: "Ledger id",
        },
        {
          name: "creditor_account_id",
          domain: "ID",
          nullable: false,
          description: "Creditor account id",
        },
        {
          name: "document_number",
          domain: "CODE(100)",
          nullable: false,
          description: "Document number",
        },
        {
          name: "document_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "invoice/credit_note/opening",
        },
        {
          name: "document_date",
          domain: "DATE",
          nullable: false,
          description: "Document date",
        },
        {
          name: "due_date",
          domain: "DATE",
          nullable: false,
          description: "Due date",
        },
        {
          name: "original_amount",
          domain: "MONEY",
          nullable: false,
          description: "Original amount",
        },
        {
          name: "journal_line_id",
          domain: "ID",
          nullable: false,
          description: "Journal line id",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["payable_item_id"],
        },
        {
          role: "business",
          members: [
            "ledger_id",
            "creditor_account_id",
            "document_number",
            "document_kind",
          ],
        },
        {
          role: "unique",
          members: ["ledger_id", "journal_line_id"],
        },
      ],
      rules:
        "PK payable_item_id; BK `(ledger_id,creditor_account_id,document_number,document_kind)`; scoped Creditor and Journal line FKs; UQ `(ledger_id,journal_line_id)`",
    },
    {
      name: "fin_settlement",
      title: "Settlement",
      description:
        "Settlement. PK settlement_id; BK `(ledger_id,settlement_number)`; exactly one subsidiary account; scoped subsidiary, optional Journal line, optional source credit-item and optional self FKs; conditional fields defined below",
      columns: [
        {
          name: "settlement_id",
          domain: "ID",
          nullable: false,
          description: "Settlement id",
        },
        {
          name: "ledger_id",
          domain: "ID",
          nullable: false,
          description: "Ledger id",
        },
        {
          name: "settlement_number",
          domain: "CODE(100)",
          nullable: false,
          description: "Settlement number",
        },
        {
          name: "settled_on",
          domain: "DATE",
          nullable: false,
          description: "Settled on",
        },
        {
          name: "settlement_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "receipt/payment/credit_application",
        },
        {
          name: "debtor_account_id",
          domain: "ID",
          nullable: true,
          description: "Debtor account id",
        },
        {
          name: "creditor_account_id",
          domain: "ID",
          nullable: true,
          description: "Creditor account id",
        },
        {
          name: "amount",
          domain: "MONEY",
          nullable: false,
          description: "Amount",
        },
        {
          name: "currency_code",
          domain: "CODE(3)",
          nullable: false,
          description: "Currency code",
        },
        {
          name: "journal_line_id",
          domain: "ID",
          nullable: true,
          description: "Journal line id",
        },
        {
          name: "source_receivable_credit_id",
          domain: "ID",
          nullable: true,
          description: "Source receivable credit id",
        },
        {
          name: "source_payable_credit_id",
          domain: "ID",
          nullable: true,
          description: "Source payable credit id",
        },
        {
          name: "reverses_settlement_id",
          domain: "ID",
          nullable: true,
          description: "Reverses settlement id",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "draft/posted/reversed",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["settlement_id"],
        },
        {
          role: "business",
          members: ["ledger_id", "settlement_number"],
        },
      ],
      rules:
        "PK settlement_id; BK `(ledger_id,settlement_number)`; exactly one subsidiary account; scoped subsidiary, optional Journal line, optional source credit-item and optional self FKs; conditional fields defined below",
    },
    {
      name: "fin_settlement_allocation",
      title: "Settlement allocation",
      description:
        "Settlement allocation. PK allocation_id; exactly one target item; UQ `(ledger_id,settlement_id,receivable_item_id)` or payable equivalent with target-aware null/predicate realization; all FKs scoped, including optional self reversal reference",
      columns: [
        {
          name: "allocation_id",
          domain: "ID",
          nullable: false,
          description: "Allocation id",
        },
        {
          name: "ledger_id",
          domain: "ID",
          nullable: false,
          description: "Ledger id",
        },
        {
          name: "settlement_id",
          domain: "ID",
          nullable: false,
          description: "Settlement id",
        },
        {
          name: "receivable_item_id",
          domain: "ID",
          nullable: true,
          description: "Receivable item id",
        },
        {
          name: "payable_item_id",
          domain: "ID",
          nullable: true,
          description: "Payable item id",
        },
        {
          name: "allocated_amount",
          domain: "MONEY",
          nullable: false,
          description: "Allocated amount",
        },
        {
          name: "allocated_on",
          domain: "DATE",
          nullable: false,
          description: "Allocated on",
        },
        {
          name: "allocation_effect",
          domain: "CODE(8)",
          nullable: false,
          description: "apply/reverse",
        },
        {
          name: "reverses_allocation_id",
          domain: "ID",
          nullable: true,
          description: "Reverses allocation id",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["allocation_id"],
        },
        {
          role: "unique",
          members: ["ledger_id", "settlement_id", "receivable_item_id"],
        },
      ],
      rules:
        "PK allocation_id; exactly one target item; UQ `(ledger_id,settlement_id,receivable_item_id)` or payable equivalent with target-aware null/predicate realization; all FKs scoped, including optional self reversal reference",
    },
    {
      name: "fin_cash_account",
      title: "Cash/bank account",
      description:
        "Cash/bank account. PK cash_account_id; BK `(ledger_id,cash_account_code)`; UQ `(ledger_id,gl_account_id)` in the initial profile; GL Account from ledger chart; no secret online-banking credentials",
      columns: [
        {
          name: "cash_account_id",
          domain: "ID",
          nullable: false,
          description: "Cash account id",
        },
        {
          name: "ledger_id",
          domain: "ID",
          nullable: false,
          description: "Ledger id",
        },
        {
          name: "cash_account_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Cash account code",
        },
        {
          name: "cash_account_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "Cash account name",
        },
        {
          name: "account_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "cash/bank",
        },
        {
          name: "currency_code",
          domain: "CODE(3)",
          nullable: false,
          description: "Currency code",
        },
        {
          name: "gl_account_id",
          domain: "ID",
          nullable: false,
          description: "Gl account id",
        },
        {
          name: "bank_label",
          domain: "TEXT(200)",
          nullable: true,
          description: "Bank label",
        },
        {
          name: "masked_reference",
          domain: "TEXT(100)",
          nullable: true,
          description: "Masked reference",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "active/closed",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["cash_account_id"],
        },
        {
          role: "business",
          members: ["ledger_id", "cash_account_code"],
        },
        {
          role: "unique",
          members: ["ledger_id", "gl_account_id"],
        },
      ],
      rules:
        "PK cash_account_id; BK `(ledger_id,cash_account_code)`; UQ `(ledger_id,gl_account_id)` in the initial profile; GL Account from ledger chart; no secret online-banking credentials",
    },
    {
      name: "fin_cash_movement",
      title: "Cash movement",
      description:
        "Cash movement. PK cash_movement_id; BK `(ledger_id,cash_account_id,movement_reference)`; scoped Cash account, Journal line and optional Settlement FKs; UQ `(ledger_id,journal_line_id)`",
      columns: [
        {
          name: "cash_movement_id",
          domain: "ID",
          nullable: false,
          description: "Cash movement id",
        },
        {
          name: "ledger_id",
          domain: "ID",
          nullable: false,
          description: "Ledger id",
        },
        {
          name: "cash_account_id",
          domain: "ID",
          nullable: false,
          description: "Cash account id",
        },
        {
          name: "movement_reference",
          domain: "CODE(100)",
          nullable: false,
          description: "Movement reference",
        },
        {
          name: "value_date",
          domain: "DATE",
          nullable: false,
          description: "Value date",
        },
        {
          name: "direction",
          domain: "CODE(8)",
          nullable: false,
          description: "receipt/payment",
        },
        {
          name: "amount",
          domain: "MONEY",
          nullable: false,
          description: "Amount",
        },
        {
          name: "journal_line_id",
          domain: "ID",
          nullable: false,
          description: "Journal line id",
        },
        {
          name: "settlement_id",
          domain: "ID",
          nullable: true,
          description: "Settlement id",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["cash_movement_id"],
        },
        {
          role: "business",
          members: ["ledger_id", "cash_account_id", "movement_reference"],
        },
        {
          role: "unique",
          members: ["ledger_id", "journal_line_id"],
        },
      ],
      rules:
        "PK cash_movement_id; BK `(ledger_id,cash_account_id,movement_reference)`; scoped Cash account, Journal line and optional Settlement FKs; UQ `(ledger_id,journal_line_id)`",
    },
    {
      name: "fin_bank_statement",
      title: "Bank statement",
      description:
        "Bank statement. PK statement_id; BK `(ledger_id,cash_account_id,statement_reference)`; scoped Cash account FK; observations distinct from ledger balances",
      columns: [
        {
          name: "statement_id",
          domain: "ID",
          nullable: false,
          description: "Statement id",
        },
        {
          name: "ledger_id",
          domain: "ID",
          nullable: false,
          description: "Ledger id",
        },
        {
          name: "cash_account_id",
          domain: "ID",
          nullable: false,
          description: "Cash account id",
        },
        {
          name: "statement_reference",
          domain: "CODE(100)",
          nullable: false,
          description: "Statement reference",
        },
        {
          name: "start_date",
          domain: "DATE",
          nullable: false,
          description: "Start date",
        },
        {
          name: "end_date",
          domain: "DATE",
          nullable: false,
          description: "End date",
        },
        {
          name: "opening_observed",
          domain: "MONEY",
          nullable: false,
          description: "Opening observed",
        },
        {
          name: "closing_observed",
          domain: "MONEY",
          nullable: false,
          description: "Closing observed",
        },
        {
          name: "import_source",
          domain: "TEXT(200)",
          nullable: false,
          description: "Import source",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["statement_id"],
        },
        {
          role: "business",
          members: ["ledger_id", "cash_account_id", "statement_reference"],
        },
      ],
      rules:
        "PK statement_id; BK `(ledger_id,cash_account_id,statement_reference)`; scoped Cash account FK; observations distinct from ledger balances",
    },
    {
      name: "fin_bank_statement_line",
      title: "Bank statement line",
      description:
        "Bank statement line. PK statement_line_id; BK `(ledger_id,statement_id,line_number)`; scoped Statement FK; sign convention positive receipt/negative payment",
      columns: [
        {
          name: "statement_line_id",
          domain: "ID",
          nullable: false,
          description: "Statement line id",
        },
        {
          name: "ledger_id",
          domain: "ID",
          nullable: false,
          description: "Ledger id",
        },
        {
          name: "statement_id",
          domain: "ID",
          nullable: false,
          description: "Statement id",
        },
        {
          name: "line_number",
          domain: "INTEGER",
          nullable: false,
          description: "Line number",
        },
        {
          name: "value_date",
          domain: "DATE",
          nullable: false,
          description: "Value date",
        },
        {
          name: "description",
          domain: "TEXT(1000)",
          nullable: true,
          description: "Description",
        },
        {
          name: "signed_amount",
          domain: "MONEY",
          nullable: false,
          description: "Signed amount",
        },
        {
          name: "bank_reference",
          domain: "TEXT(200)",
          nullable: true,
          description: "Bank reference",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["statement_line_id"],
        },
        {
          role: "business",
          members: ["ledger_id", "statement_id", "line_number"],
        },
      ],
      rules:
        "PK statement_line_id; BK `(ledger_id,statement_id,line_number)`; scoped Statement FK; sign convention positive receipt/negative payment",
    },
    {
      name: "fin_bank_match",
      title: "Bank match",
      description:
        "Bank match. PK bank_match_id; UQ `(ledger_id,statement_line_id,cash_movement_id)`; scoped Statement line and Cash movement FKs; positive matched magnitude",
      columns: [
        {
          name: "bank_match_id",
          domain: "ID",
          nullable: false,
          description: "Bank match id",
        },
        {
          name: "ledger_id",
          domain: "ID",
          nullable: false,
          description: "Ledger id",
        },
        {
          name: "statement_line_id",
          domain: "ID",
          nullable: false,
          description: "Statement line id",
        },
        {
          name: "cash_movement_id",
          domain: "ID",
          nullable: false,
          description: "Cash movement id",
        },
        {
          name: "matched_amount",
          domain: "MONEY",
          nullable: false,
          description: "Matched amount",
        },
        {
          name: "reviewed_at",
          domain: "INSTANT",
          nullable: false,
          description: "Reviewed at",
        },
        {
          name: "match_method",
          domain: "CODE(16)",
          nullable: false,
          description: "manual/approved_rule",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["bank_match_id"],
        },
        {
          role: "unique",
          members: ["ledger_id", "statement_line_id", "cash_movement_id"],
        },
      ],
      rules:
        "PK bank_match_id; UQ `(ledger_id,statement_line_id,cash_movement_id)`; scoped Statement line and Cash movement FKs; positive matched magnitude",
    },
    {
      name: "fin_report_line",
      title: "Financial report line",
      description:
        "Financial report line. PK report_line_id; BK `(ledger_id,report_code,line_code)`; optional scoped self FK; parent must have same report_code; hierarchy cycles prohibited",
      columns: [
        {
          name: "report_line_id",
          domain: "ID",
          nullable: false,
          description: "Report line id",
        },
        {
          name: "ledger_id",
          domain: "ID",
          nullable: false,
          description: "Ledger id",
        },
        {
          name: "report_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Report code",
        },
        {
          name: "line_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Line code",
        },
        {
          name: "line_label",
          domain: "TEXT(200)",
          nullable: false,
          description: "Line label",
        },
        {
          name: "parent_line_id",
          domain: "ID",
          nullable: true,
          description: "Parent line id",
        },
        {
          name: "line_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "detail/subtotal",
        },
        {
          name: "display_order",
          domain: "INTEGER",
          nullable: false,
          description: "Display order",
        },
        {
          name: "sign_multiplier",
          domain: "INTEGER",
          nullable: false,
          description: "either 1 or -1",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["report_line_id"],
        },
        {
          role: "business",
          members: ["ledger_id", "report_code", "line_code"],
        },
      ],
      rules:
        "PK report_line_id; BK `(ledger_id,report_code,line_code)`; optional scoped self FK; parent must have same report_code; hierarchy cycles prohibited",
    },
    {
      name: "fin_account_report_map",
      title: "Account report mapping",
      description:
        "Account report mapping. PK mapping_id; BK `(ledger_id,account_id,report_line_id,valid_from)`; scoped Report line FK; Account must belong to ledger chart; report target must be a detail line",
      columns: [
        {
          name: "mapping_id",
          domain: "ID",
          nullable: false,
          description: "Mapping id",
        },
        {
          name: "ledger_id",
          domain: "ID",
          nullable: false,
          description: "Ledger id",
        },
        {
          name: "account_id",
          domain: "ID",
          nullable: false,
          description: "Account id",
        },
        {
          name: "report_line_id",
          domain: "ID",
          nullable: false,
          description: "Report line id",
        },
        {
          name: "valid_from",
          domain: "DATE",
          nullable: false,
          description: "Valid from",
        },
        {
          name: "valid_to",
          domain: "DATE",
          nullable: true,
          description: "Valid to",
        },
        {
          name: "allocation_percent",
          domain: "PCT",
          nullable: false,
          description: "default 100",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["mapping_id"],
        },
        {
          role: "business",
          members: ["ledger_id", "account_id", "report_line_id", "valid_from"],
        },
      ],
      rules:
        "PK mapping_id; BK `(ledger_id,account_id,report_line_id,valid_from)`; scoped Report line FK; Account must belong to ledger chart; report target must be a detail line",
    },
  ],
  assets: [
    {
      name: "ast_asset",
      title: "Asset",
      description:
        "Asset. PK asset_id; BK `(organization_id,asset_tag)`; scoped optional org_location FK; serial number not assumed globally unique",
      columns: [
        {
          name: "asset_id",
          domain: "ID",
          nullable: false,
          description: "Asset id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "asset_tag",
          domain: "CODE(64)",
          nullable: false,
          description: "Asset tag",
        },
        {
          name: "asset_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "Asset name",
        },
        {
          name: "serial_number",
          domain: "TEXT(100)",
          nullable: true,
          description: "Serial number",
        },
        {
          name: "location_id",
          domain: "ID",
          nullable: true,
          description: "Location id",
        },
        {
          name: "in_service_date",
          domain: "DATE",
          nullable: true,
          description: "In service date",
        },
        {
          name: "retired_on",
          domain: "DATE",
          nullable: true,
          description: "Retired on",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "planned/in_service/out_of_service/retired",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["asset_id"],
        },
        {
          role: "business",
          members: ["organization_id", "asset_tag"],
        },
      ],
      rules:
        "PK asset_id; BK `(organization_id,asset_tag)`; scoped optional org_location FK; serial number not assumed globally unique",
    },
    {
      name: "ast_plan",
      title: "Maintenance plan",
      description:
        "Maintenance plan. PK maintenance_plan_id; BK `(organization_id,asset_id,plan_code,valid_from)`; scoped Asset FK; interval fields both present or absent",
      columns: [
        {
          name: "maintenance_plan_id",
          domain: "ID",
          nullable: false,
          description: "Maintenance plan id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "asset_id",
          domain: "ID",
          nullable: false,
          description: "Asset id",
        },
        {
          name: "plan_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Plan code",
        },
        {
          name: "description",
          domain: "TEXT(2000)",
          nullable: false,
          description: "Description",
        },
        {
          name: "interval_quantity",
          domain: "DECIMAL(12,2)",
          nullable: true,
          description: "Interval quantity",
        },
        {
          name: "interval_unit",
          domain: "CODE(32)",
          nullable: true,
          description: "Interval unit",
        },
        {
          name: "valid_from",
          domain: "DATE",
          nullable: false,
          description: "Valid from",
        },
        {
          name: "valid_to",
          domain: "DATE",
          nullable: true,
          description: "Valid to",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["maintenance_plan_id"],
        },
        {
          role: "business",
          members: ["organization_id", "asset_id", "plan_code", "valid_from"],
        },
      ],
      rules:
        "PK maintenance_plan_id; BK `(organization_id,asset_id,plan_code,valid_from)`; scoped Asset FK; interval fields both present or absent",
    },
    {
      name: "ast_work_order",
      title: "Maintenance work order",
      description:
        "Maintenance work order. PK work_order_id; BK `(organization_id,work_order_number)`; scoped Asset/optional Plan FKs; plan must be for asset",
      columns: [
        {
          name: "work_order_id",
          domain: "ID",
          nullable: false,
          description: "Work order id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "asset_id",
          domain: "ID",
          nullable: false,
          description: "Asset id",
        },
        {
          name: "maintenance_plan_id",
          domain: "ID",
          nullable: true,
          description: "Maintenance plan id",
        },
        {
          name: "work_order_number",
          domain: "CODE(64)",
          nullable: false,
          description: "Work order number",
        },
        {
          name: "requested_on",
          domain: "DATE",
          nullable: false,
          description: "Requested on",
        },
        {
          name: "due_on",
          domain: "DATE",
          nullable: true,
          description: "Due on",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "requested/planned/in_progress/completed/cancelled",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["work_order_id"],
        },
        {
          role: "business",
          members: ["organization_id", "work_order_number"],
        },
      ],
      rules:
        "PK work_order_id; BK `(organization_id,work_order_number)`; scoped Asset/optional Plan FKs; plan must be for asset",
    },
    {
      name: "ast_task",
      title: "Maintenance task",
      description:
        "Maintenance task. PK maintenance_task_id; BK `(organization_id,work_order_id,task_number)`; scoped Work order FK",
      columns: [
        {
          name: "maintenance_task_id",
          domain: "ID",
          nullable: false,
          description: "Maintenance task id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "work_order_id",
          domain: "ID",
          nullable: false,
          description: "Work order id",
        },
        {
          name: "task_number",
          domain: "INTEGER",
          nullable: false,
          description: "Task number",
        },
        {
          name: "description",
          domain: "TEXT(1000)",
          nullable: false,
          description: "Description",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "planned/in_progress/done/skipped",
        },
        {
          name: "completed_at",
          domain: "INSTANT",
          nullable: true,
          description: "Completed at",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["maintenance_task_id"],
        },
        {
          role: "business",
          members: ["organization_id", "work_order_id", "task_number"],
        },
      ],
      rules:
        "PK maintenance_task_id; BK `(organization_id,work_order_id,task_number)`; scoped Work order FK",
    },
    {
      name: "ast_service_event",
      title: "Service event",
      description:
        "Service event. PK service_event_id; scoped Work order FK; downtime nonnegative; actor can bind an approved employee/supplier port",
      columns: [
        {
          name: "service_event_id",
          domain: "ID",
          nullable: false,
          description: "Service event id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "work_order_id",
          domain: "ID",
          nullable: false,
          description: "Work order id",
        },
        {
          name: "occurred_at",
          domain: "INSTANT",
          nullable: false,
          description: "Occurred at",
        },
        {
          name: "service_description",
          domain: "TEXT(2000)",
          nullable: false,
          description: "Service description",
        },
        {
          name: "performed_by_reference",
          domain: "TEXT(200)",
          nullable: true,
          description: "Performed by reference",
        },
        {
          name: "downtime_hours",
          domain: "DECIMAL(12,4)",
          nullable: true,
          description: "Downtime hours",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["service_event_id"],
        },
      ],
      rules:
        "PK service_event_id; scoped Work order FK; downtime nonnegative; actor can bind an approved employee/supplier port",
    },
    {
      name: "ast_meter_reading",
      title: "Meter reading",
      description:
        "Meter reading. PK meter_reading_id; BK `(organization_id,asset_id,meter_code,observed_at)`; scoped Asset FK; reset/rollover policy explicit",
      columns: [
        {
          name: "meter_reading_id",
          domain: "ID",
          nullable: false,
          description: "Meter reading id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "asset_id",
          domain: "ID",
          nullable: false,
          description: "Asset id",
        },
        {
          name: "meter_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Meter code",
        },
        {
          name: "observed_at",
          domain: "INSTANT",
          nullable: false,
          description: "Observed at",
        },
        {
          name: "reading",
          domain: "QTY",
          nullable: false,
          description: "Reading",
        },
        {
          name: "unit_code",
          domain: "CODE(32)",
          nullable: false,
          description: "Unit code",
        },
        {
          name: "reset_reason",
          domain: "TEXT(500)",
          nullable: true,
          description: "Reset reason",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["meter_reading_id"],
        },
        {
          role: "business",
          members: ["organization_id", "asset_id", "meter_code", "observed_at"],
        },
      ],
      rules:
        "PK meter_reading_id; BK `(organization_id,asset_id,meter_code,observed_at)`; scoped Asset FK; reset/rollover policy explicit",
    },
  ],
  projects: [
    {
      name: "prj_project",
      title: "Project",
      description:
        "Project. PK project_id; BK `(organization_id,project_code)`; optional date ordering rule",
      columns: [
        {
          name: "project_id",
          domain: "ID",
          nullable: false,
          description: "Project id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "project_code",
          domain: "CODE(64)",
          nullable: false,
          description: "Project code",
        },
        {
          name: "project_name",
          domain: "TEXT(200)",
          nullable: false,
          description: "Project name",
        },
        {
          name: "description",
          domain: "TEXT(2000)",
          nullable: true,
          description: "Description",
        },
        {
          name: "start_date",
          domain: "DATE",
          nullable: true,
          description: "Start date",
        },
        {
          name: "end_date",
          domain: "DATE",
          nullable: true,
          description: "End date",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "proposed/active/on_hold/completed/cancelled",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["project_id"],
        },
        {
          role: "business",
          members: ["organization_id", "project_code"],
        },
      ],
      rules:
        "PK project_id; BK `(organization_id,project_code)`; optional date ordering rule",
    },
    {
      name: "prj_work_item",
      title: "Work item",
      description:
        "Work item. PK work_item_id; BK `(organization_id,project_id,work_item_code)`; scoped Project and optional self FK; parent must be same project",
      columns: [
        {
          name: "work_item_id",
          domain: "ID",
          nullable: false,
          description: "Work item id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "project_id",
          domain: "ID",
          nullable: false,
          description: "Project id",
        },
        {
          name: "work_item_code",
          domain: "CODE(64)",
          nullable: false,
          description: "Work item code",
        },
        {
          name: "title",
          domain: "TEXT(200)",
          nullable: false,
          description: "Title",
        },
        {
          name: "work_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "task/deliverable/milestone",
        },
        {
          name: "parent_work_item_id",
          domain: "ID",
          nullable: true,
          description: "Parent work item id",
        },
        {
          name: "planned_start",
          domain: "DATE",
          nullable: true,
          description: "Planned start",
        },
        {
          name: "planned_end",
          domain: "DATE",
          nullable: true,
          description: "Planned end",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "planned/active/blocked/done/cancelled",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["work_item_id"],
        },
        {
          role: "business",
          members: ["organization_id", "project_id", "work_item_code"],
        },
      ],
      rules:
        "PK work_item_id; BK `(organization_id,project_id,work_item_code)`; scoped Project and optional self FK; parent must be same project",
    },
    {
      name: "prj_dependency",
      title: "Work dependency",
      description:
        "Work dependency. PK dependency_id; BK `(organization_id,predecessor_work_item_id,successor_work_item_id,dependency_kind)`; scoped Work item FKs; distinct endpoints",
      columns: [
        {
          name: "dependency_id",
          domain: "ID",
          nullable: false,
          description: "Dependency id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "predecessor_work_item_id",
          domain: "ID",
          nullable: false,
          description: "Predecessor work item id",
        },
        {
          name: "successor_work_item_id",
          domain: "ID",
          nullable: false,
          description: "Successor work item id",
        },
        {
          name: "dependency_kind",
          domain: "CODE(16)",
          nullable: false,
          description: "finish_start/start_start/finish_finish/start_finish",
        },
        {
          name: "lag_hours",
          domain: "DECIMAL(12,4)",
          nullable: false,
          description: "Lag hours",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["dependency_id"],
        },
        {
          role: "business",
          members: [
            "organization_id",
            "predecessor_work_item_id",
            "successor_work_item_id",
            "dependency_kind",
          ],
        },
      ],
      rules:
        "PK dependency_id; BK `(organization_id,predecessor_work_item_id,successor_work_item_id,dependency_kind)`; scoped Work item FKs; distinct endpoints",
    },
    {
      name: "prj_assignment",
      title: "Work assignment",
      description:
        "Work assignment. PK work_assignment_id; BK `(organization_id,work_item_id,employment_id,assignment_role,valid_from)`; scoped Work item and Employment FKs",
      columns: [
        {
          name: "work_assignment_id",
          domain: "ID",
          nullable: false,
          description: "Work assignment id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "work_item_id",
          domain: "ID",
          nullable: false,
          description: "Work item id",
        },
        {
          name: "employment_id",
          domain: "ID",
          nullable: false,
          description: "Employment id",
        },
        {
          name: "assignment_role",
          domain: "CODE(32)",
          nullable: false,
          description: "Assignment role",
        },
        {
          name: "valid_from",
          domain: "DATE",
          nullable: false,
          description: "Valid from",
        },
        {
          name: "valid_to",
          domain: "DATE",
          nullable: true,
          description: "Valid to",
        },
        {
          name: "allocation_percent",
          domain: "PCT",
          nullable: true,
          description: "Allocation percent",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["work_assignment_id"],
        },
        {
          role: "business",
          members: [
            "organization_id",
            "work_item_id",
            "employment_id",
            "assignment_role",
            "valid_from",
          ],
        },
      ],
      rules:
        "PK work_assignment_id; BK `(organization_id,work_item_id,employment_id,assignment_role,valid_from)`; scoped Work item and Employment FKs",
    },
    {
      name: "prj_work_log",
      title: "Work log",
      description:
        "Work log. PK work_log_id; scoped Assignment FK; quantity positive; date within assignment policy",
      columns: [
        {
          name: "work_log_id",
          domain: "ID",
          nullable: false,
          description: "Work log id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "work_assignment_id",
          domain: "ID",
          nullable: false,
          description: "Work assignment id",
        },
        {
          name: "work_date",
          domain: "DATE",
          nullable: false,
          description: "Work date",
        },
        {
          name: "quantity",
          domain: "QTY",
          nullable: false,
          description: "Quantity",
        },
        {
          name: "unit_code",
          domain: "CODE(32)",
          nullable: false,
          description: "default hour",
        },
        {
          name: "description",
          domain: "TEXT(1000)",
          nullable: true,
          description: "Description",
        },
        {
          name: "status",
          domain: "CODE(16)",
          nullable: false,
          description: "draft/submitted/approved/rejected",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["work_log_id"],
        },
      ],
      rules:
        "PK work_log_id; scoped Assignment FK; quantity positive; date within assignment policy",
    },
    {
      name: "prj_status_event",
      title: "Project status event",
      description:
        "Project status event. PK status_event_id; scoped Project FK; event/state consistency is explicit runtime rule",
      columns: [
        {
          name: "status_event_id",
          domain: "ID",
          nullable: false,
          description: "Status event id",
        },
        {
          name: "organization_id",
          domain: "ID",
          nullable: false,
          description: "Organization id",
        },
        {
          name: "project_id",
          domain: "ID",
          nullable: false,
          description: "Project id",
        },
        {
          name: "occurred_at",
          domain: "INSTANT",
          nullable: false,
          description: "Occurred at",
        },
        {
          name: "from_status",
          domain: "CODE(16)",
          nullable: true,
          description: "From status",
        },
        {
          name: "to_status",
          domain: "CODE(16)",
          nullable: false,
          description: "To status",
        },
        {
          name: "reason",
          domain: "TEXT(1000)",
          nullable: false,
          description: "Reason",
        },
      ],
      keys: [
        {
          role: "primary",
          members: ["status_event_id"],
        },
      ],
      rules:
        "PK status_event_id; scoped Project FK; event/state consistency is explicit runtime rule",
    },
  ],
};
